import React, { useState, useEffect, useRef } from 'react';
import { SiteHeader, SiteFooter } from '../components/SiteChrome';
import {
  ArrowRight, ArrowDown, Check, ChevronLeft, ChevronRight, ShieldCheck, Atom, Layers,
  Cpu, Mountain, Wind, Droplets, Zap, Radio, Lock, Globe2, ShieldAlert, Building2, Boxes,
  UploadCloud, FileText, Image as ImageIcon, X, Loader2,
  Battery, Sun, Fuel, Gauge, Leaf, Recycle, Filter, Waves, Wrench, Thermometer, HeartPulse, Plus,
  Server, Database, BookOpen, GraduationCap, Landmark, Archive,
  Factory, Truck, MapPin, Maximize2, Network, PackageCheck
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const IMG = {
  hero: 'https://static.prod-images.emergentagent.com/jobs/4cd02427-b7f1-4429-bc2d-baceb69a3fd9/images/483f44c405e48de3d6d821e5db0354c9de927290c5dc52dbea5cbef0d64aa821.jpeg',
  wfortex: 'https://static.prod-images.emergentagent.com/jobs/4cd02427-b7f1-4429-bc2d-baceb69a3fd9/images/7608e0a7c8f99ed1e698717706305b289ba8d66d8f6c2d85dc783a68d6f0bc8a.jpeg',
  concrete: 'https://static.prod-images.emergentagent.com/jobs/4cd02427-b7f1-4429-bc2d-baceb69a3fd9/images/944ac742e774142ce1b71d81adcff949d68af0daf72a985f876861fcc9c1d9cc.jpeg',
  nuclear: 'https://static.prod-images.emergentagent.com/jobs/4cd02427-b7f1-4429-bc2d-baceb69a3fd9/images/66b9b61296680613ceaf6dada39306f3a65c9dc82c85215d3caacb62e9f8f9d3.jpeg',
  interior: 'https://static.prod-images.emergentagent.com/jobs/4cd02427-b7f1-4429-bc2d-baceb69a3fd9/images/18de68957d8ccfb9efd034df6d8a2596c275ceecc3fc08fb86f3f9dc4fbf0dbb.jpeg',
  spa: 'https://static.prod-images.emergentagent.com/jobs/4cd02427-b7f1-4429-bc2d-baceb69a3fd9/images/0c867eca49c2061c19aac2fdf16d598d4c42deb9c943227b74e243f451d0aa21.jpeg',
};

const GOLD = '#D4A84B';

const partners = [
  { name: 'TRIUMPH Alliance', src: '/logos/triumph.png', url: 'https://triumph-alliance.com/' },
  { name: 'WITKOWITZ', src: '/logos/witkowitz.png', url: 'https://witkowitz.eu/' },
  { name: 'IBIPC', src: '/logos/ibipc.png', url: 'https://www.ibipc.com/' },
  { name: 'Institute for Safety Problems of Nuclear Power Plants, NAS of Ukraine', src: '/logos/ispnpp.png', url: 'https://www.ispnpp.kiev.ua/' },
  { name: 'MIFRE HOLDINGS', src: '/logos/mifre.png', url: 'https://www.mifre.cz/' },
  { name: 'RIZZO ASSOCIATES', src: '/logos/rizzo.png', url: 'https://www.rizzoassoc.cz/?lang=en', shrink: true },
  { name: 'VVÚ', src: '/logos/vvu.png', url: 'https://www.vvu.cz/', boost: true, shrink: true },
  { name: 'AFRY', src: '/logos/afry.png', url: 'https://afry.com/cs-cz', shrink: true },
  { name: 'BunkerDeluxe', src: '/logos/bunkerdeluxe.png', url: 'https://www.bunkerdeluxe.com/', boost: true },
  { name: 'W-FORTEX', src: '/logos/wfortex.png', url: 'https://triumph-alliance.com/programs/w-fortex', boost: true },
];

const useReveal = (dep) => {
  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('in-view'); obs.unobserve(e.target); } });
    }, { threshold: 0, rootMargin: '0px 0px -60px 0px' });
    document.querySelectorAll('.reveal').forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [dep]);
};

const Counter = ({ value, decimals = 0, suffix = '' }) => {
  const [n, setN] = useState(0); const ref = useRef(null); const started = useRef(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver((entries) => entries.forEach((e) => {
      if (e.isIntersecting && !started.current) {
        started.current = true; const dur = 1400, s = performance.now();
        const tick = (now) => { const p = Math.min((now - s) / dur, 1); setN(p * value); if (p < 1) requestAnimationFrame(tick); };
        requestAnimationFrame(tick);
      }
    }), { threshold: 0.5 });
    obs.observe(el); return () => obs.disconnect();
  }, [value]);
  return <span ref={ref}>{n.toFixed(decimals)}{suffix}</span>;
};

const dna = [
  { icon: Atom, t: 'Nuclear Energy' }, { icon: Building2, t: 'Critical Infrastructure' },
  { icon: ShieldCheck, t: 'Defence Infrastructure' }, { icon: ShieldAlert, t: 'Protective Engineering' },
  { icon: Mountain, t: 'Underground Structures' }, { icon: Layers, t: 'Advanced Materials' },
  { icon: Cpu, t: 'Autonomous Systems' },
];

const spaces = ['Private Suites', 'Family Living', 'Kitchen', 'Dining', 'Office', 'Secure Communications',
  'Medical Room', 'Fitness', 'Spa', 'Cinema', 'Wine Storage', 'Valuables Storage', 'Security Control',
  'Staff Accommodation', 'Pet Facilities', 'Technical Rooms'];

const autonomySys = [
  { icon: Zap, t: 'Energy' }, { icon: Droplets, t: 'Water' }, { icon: Wind, t: 'Air' },
  { icon: Layers, t: 'Climate' }, { icon: Radio, t: 'Communications' }, { icon: Droplets, t: 'Sanitation' },
  { icon: Boxes, t: 'Food Storage' }, { icon: Lock, t: 'Security' },
];

const energyItems = [
  { icon: Zap, t: 'Independent Power Generation' },
  { icon: Battery, t: 'Energy Storage' },
  { icon: Leaf, t: 'Renewable Energy Sources' },
  { icon: Sun, t: 'Solar & Wind Integration' },
  { icon: Fuel, t: 'Conventional Backup Generation' },
  { icon: Layers, t: 'Multiple Redundant Energy Systems' },
  { icon: Gauge, t: 'Advanced Energy Management' },
  { icon: Cpu, t: 'Future-Ready Energy Infrastructure' },
];

const waterItems = [
  { icon: Droplets, t: 'Independent Water Sources' },
  { icon: Filter, t: 'Water Treatment & Purification' },
  { icon: Boxes, t: 'Strategic Water Storage' },
  { icon: Layers, t: 'Advanced Filtration' },
  { icon: Recycle, t: 'Water Recycling & Reuse' },
  { icon: Wrench, t: 'Alternative Water Supply Technologies' },
  { icon: Waves, t: 'Desalination Technologies*' },
];

const integratedSystems = [
  { icon: Zap, t: 'Energy' }, { icon: Droplets, t: 'Water' }, { icon: Wind, t: 'Air' },
  { icon: Thermometer, t: 'Climate' }, { icon: Radio, t: 'Communications' },
  { icon: Recycle, t: 'Sanitation' }, { icon: HeartPulse, t: 'Life Support' },
];

const protection = ['Blast Protection', 'Ballistic Protection', 'CBRN Protection', 'Air Filtration',
  'Structural Hardening', 'Secure Access', 'Fire Protection', 'Redundant Utilities', 'Secure Communications'];

const turnkey = ['Site Assessment', 'Risk & Threat Analysis', 'Concept', 'Architecture', 'Protective Engineering',
  'Structural Design', 'Underground Construction', 'MEP & Autonomous Systems', 'Interior Architecture',
  'System Integration', 'Testing', 'Commissioning'];

const Section = ({ id, className = '', children }) => (
  <section id={id} className={`reveal ${className}`}>{children}</section>
);

const TechOverlayImage = ({ src, variant = 'a', className = '', testid }) => {
  const g = 'rgba(212,168,75,0.55)';
  const s = 'rgba(143,176,189,0.45)';
  const t = 'rgba(212,168,75,0.75)';
  return (
    <div className={`relative min-h-[380px] overflow-hidden ${className}`} data-testid={testid}>
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${src})` }} />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(8,9,11,0.58), rgba(8,9,11,0.78))' }} />
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 380" preserveAspectRatio="none" aria-hidden="true">
        {[80, 160, 240, 320].map((x, i) => (<line key={`v${i}`} x1={x} y1="0" x2={x} y2="380" stroke={s} strokeWidth="0.5" opacity="0.35" />))}
        {[95, 190, 285].map((y, i) => (<line key={`h${i}`} x1="0" y1={y} x2="400" y2={y} stroke={s} strokeWidth="0.5" opacity="0.35" />))}
        {variant === 'a' ? (
          <g>
            <line x1="40" y1="40" x2="210" y2="40" stroke={g} strokeWidth="0.8" />
            <line x1="40" y1="34" x2="40" y2="46" stroke={g} strokeWidth="0.8" /><line x1="210" y1="34" x2="210" y2="46" stroke={g} strokeWidth="0.8" />
            <text x="125" y="34" fill={t} fontSize="7" textAnchor="middle" letterSpacing="1.5">SECTION A–A</text>
            <circle cx="300" cy="120" r="15" fill="none" stroke={g} strokeWidth="0.8" />
            <line x1="278" y1="120" x2="322" y2="120" stroke={g} strokeWidth="0.7" /><line x1="300" y1="98" x2="300" y2="142" stroke={g} strokeWidth="0.7" />
            <text x="300" y="152" fill={t} fontSize="6.5" textAnchor="middle" letterSpacing="1">SEC·PT 01</text>
            <path d="M16 16 H44 M16 16 V44" fill="none" stroke={g} strokeWidth="1" />
            <path d="M384 364 H356 M384 364 V336" fill="none" stroke={g} strokeWidth="1" />
            <line x1="150" y1="250" x2="92" y2="316" stroke={g} strokeWidth="0.7" /><circle cx="92" cy="316" r="3" fill="none" stroke={g} strokeWidth="0.8" />
            <text x="80" y="332" fill={t} fontSize="6.5" letterSpacing="1">LVL −3</text>
            <path d="M345 60 V150 M341 66 L345 60 L349 66 M341 144 L345 150 L349 144" fill="none" stroke={s} strokeWidth="0.7" opacity="0.7" />
          </g>
        ) : (
          <g>
            <line x1="46" y1="70" x2="46" y2="210" stroke={g} strokeWidth="0.8" />
            <line x1="40" y1="70" x2="52" y2="70" stroke={g} strokeWidth="0.8" /><line x1="40" y1="210" x2="52" y2="210" stroke={g} strokeWidth="0.8" />
            <text x="60" y="142" fill={t} fontSize="7" letterSpacing="1.5">ENV·ZONE</text>
            <circle cx="120" cy="150" r="15" fill="none" stroke={g} strokeWidth="0.8" />
            <line x1="98" y1="150" x2="142" y2="150" stroke={g} strokeWidth="0.7" /><line x1="120" y1="128" x2="120" y2="172" stroke={g} strokeWidth="0.7" />
            <path d="M356 16 H384 M384 16 V44" fill="none" stroke={g} strokeWidth="1" />
            <path d="M16 364 H44 M16 364 V336" fill="none" stroke={g} strokeWidth="1" />
            <line x1="260" y1="120" x2="330" y2="78" stroke={g} strokeWidth="0.7" /><circle cx="330" cy="78" r="3" fill="none" stroke={g} strokeWidth="0.8" />
            <text x="316" y="70" fill={t} fontSize="6.5" letterSpacing="1">CTRL·04</text>
            <circle cx="300" cy="300" r="10" fill="none" stroke={s} strokeWidth="0.7" opacity="0.7" /><circle cx="300" cy="300" r="18" fill="none" stroke={s} strokeWidth="0.5" opacity="0.5" />
            <path d="M60 300 H240 M66 296 L60 300 L66 304 M234 296 L240 300 L234 304" fill="none" stroke={s} strokeWidth="0.7" opacity="0.7" />
          </g>
        )}
      </svg>
    </div>
  );
};

const WATERCLR = '#6FA8C7';
const AutonomyDiagram = () => (
  <div className="mt-14 border border-zinc-800 rounded-sm bg-[#0a0b0d] p-5 sm:p-8 overflow-hidden" data-testid="autonomy-diagram">
    <style>{`
      @keyframes ulbFlow { to { stroke-dashoffset: -100; } }
      .ulb-flow { stroke-dasharray: 4 10; animation: ulbFlow 2.4s linear infinite; }
      .ulb-flow.slow { animation-duration: 3.4s; }
      @keyframes ulbPulse { 0%,100%{ opacity:.4 } 50%{ opacity:1 } }
      .ulb-pulse { animation: ulbPulse 3.2s ease-in-out infinite; }
    `}</style>
    <svg viewBox="0 0 1000 320" className="w-full h-auto" role="img" aria-label="ULB energy and water autonomy flow schematic">
      <defs>
        <path id="ulbPathE" d="M110 88 H520 C640 88 660 160 760 160" fill="none" />
        <path id="ulbPathW" d="M110 232 H520 C640 232 660 160 760 160" fill="none" />
        <marker id="ulbArrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill={GOLD} />
        </marker>
      </defs>

      <text x="40" y="60" fill={GOLD} fontSize="12" letterSpacing="3" fontWeight="700">ENERGY</text>
      <text x="40" y="262" fill={WATERCLR} fontSize="12" letterSpacing="3" fontWeight="700">WATER</text>

      <use href="#ulbPathE" stroke="#1f2937" strokeWidth="2" />
      <use href="#ulbPathE" stroke={GOLD} strokeWidth="2" className="ulb-flow" />
      {[[110,'GENERATION'],[315,'STORAGE'],[520,'DISTRIBUTION']].map(([x,l],i)=>(
        <g key={i}>
          <circle cx={x} cy="88" r="6" fill="#0a0b0d" stroke={GOLD} strokeWidth="2" className="ulb-pulse" style={{ animationDelay: `${i*0.4}s` }} />
          <text x={x} y="118" fill="#a1a1aa" fontSize="11" textAnchor="middle" letterSpacing="1">{l}</text>
        </g>
      ))}

      <use href="#ulbPathW" stroke="#1f2937" strokeWidth="2" />
      <use href="#ulbPathW" stroke={WATERCLR} strokeWidth="2" className="ulb-flow slow" />
      {[[110,'SOURCE'],[315,'TREATMENT'],[520,'STORAGE']].map(([x,l],i)=>(
        <g key={i}>
          <circle cx={x} cy="232" r="6" fill="#0a0b0d" stroke={WATERCLR} strokeWidth="2" className="ulb-pulse" style={{ animationDelay: `${i*0.4+0.2}s` }} />
          <text x={x} y="214" fill="#a1a1aa" fontSize="11" textAnchor="middle" letterSpacing="1">{l}</text>
        </g>
      ))}

      <circle r="4" fill={GOLD}>
        <animateMotion dur="3s" repeatCount="indefinite"><mpath href="#ulbPathE" /></animateMotion>
      </circle>
      <circle r="4" fill={WATERCLR}>
        <animateMotion dur="4s" repeatCount="indefinite"><mpath href="#ulbPathW" /></animateMotion>
      </circle>

      <g>
        <polygon points="760,120 795,140 795,180 760,200 725,180 725,140" fill="#0c0e11" stroke={GOLD} strokeWidth="2" />
        <polygon points="760,120 795,140 795,180 760,200 725,180 725,140" fill="none" stroke={GOLD} strokeWidth="6" opacity="0.12" />
        <text x="760" y="157" fill="#ffffff" fontSize="16" textAnchor="middle" fontWeight="700" fontFamily="Syne, sans-serif">ULB</text>
        <text x="760" y="174" fill={GOLD} fontSize="8" textAnchor="middle" letterSpacing="2">CORE</text>
      </g>

      <line x1="795" y1="160" x2="905" y2="160" stroke={GOLD} strokeWidth="2" className="ulb-flow" markerEnd="url(#ulbArrow)" />
      <text x="850" y="146" fill="#a1a1aa" fontSize="10" textAnchor="middle" letterSpacing="1">AUTONOMY</text>
      <text x="850" y="186" fill="#71717a" fontSize="8" textAnchor="middle" letterSpacing="1">AIR · CLIMATE</text>
      <text x="850" y="198" fill="#71717a" fontSize="8" textAnchor="middle" letterSpacing="1">COMMS · LIFE</text>
    </svg>
    <p className="mt-4 text-xs text-zinc-600 text-center">Illustrative schematic — every ULB energy and water architecture is individually engineered with redundancy and independence appropriate to the project.</p>
  </div>
);

const Kicker = ({ children }) => (
  <p className="uppercase tracking-[0.3em] text-[11px] mb-4" style={{ color: GOLD }}>{children}</p>
);

const AIRCLR = '#7FBFA8';
const WASTECLR = '#C79A5B';
const DATACLR = '#9AA6D0';
const COMMSCLR = '#8FB0BD';

const UndergroundDiagram = () => {
  const zones = [
    { x: 205, y: 212, l: 'PROTECTED ACCESS' },
    { x: 312, y: 274, l: 'TRANSITION / DECON' },
    { x: 452, y: 216, l: 'RESIDENTIAL CORE' },
    { x: 566, y: 312, l: 'TECHNICAL CORE' },
    { x: 690, y: 262, l: 'STORAGE' },
    { x: 793, y: 332, l: 'AUTONOMY SYSTEMS' },
  ];
  const rails = [
    { y: 548, c: GOLD, l: 'ENERGY', s: 'GEN · STORE · DIST' },
    { y: 572, c: WATERCLR, l: 'WATER', s: 'SOURCE · TREAT · STORE · DIST' },
    { y: 596, c: AIRCLR, l: 'AIR & CLIMATE', s: 'INTAKE · FILTER · TREAT · CIRCULATE' },
    { y: 620, c: WASTECLR, l: 'WASTE & RESOURCES', s: 'COLLECT · TREAT · RECOVERY' },
  ];
  const dataTargets = [
    { x: 452, y: 216, l: 'RESIDENTIAL' },
    { x: 400, y: 356, l: 'EDUCATION' },
    { x: 662, y: 356, l: 'CONTROL' },
    { x: 725, y: 408, l: 'COMMS' },
  ];
  const legend = [
    { c: GOLD, l: 'Energy' }, { c: WATERCLR, l: 'Water' }, { c: AIRCLR, l: 'Air & Climate' },
    { c: WASTECLR, l: 'Waste & Recovery' }, { c: DATACLR, l: 'Data & Knowledge' },
  ];
  const zonesM = [
    { x: 150, y: 150, l: 'PROTECTED ACCESS' },
    { x: 270, y: 228, l: 'TRANSITION' },
    { x: 150, y: 306, l: 'RESIDENTIAL', big: true },
    { x: 270, y: 386, l: 'TECHNICAL CORE' },
    { x: 150, y: 470, l: 'STORAGE' },
    { x: 270, y: 550, l: 'AUTONOMY' },
  ];
  const railsM = [
    { y: 632, c: GOLD, l: 'ENERGY' },
    { y: 662, c: WATERCLR, l: 'WATER' },
    { y: 692, c: AIRCLR, l: 'AIR' },
    { y: 722, c: WASTECLR, l: 'WASTE' },
  ];
  return (
    <div className="mt-8 border border-zinc-800 rounded-sm bg-[#0a0b0d] p-5 sm:p-8 overflow-hidden" data-testid="underground-diagram">
      <style>{`
        @keyframes uwFlow { to { stroke-dashoffset: -120; } }
        .uw-flow { stroke-dasharray: 4 10; animation: uwFlow 2.6s linear infinite; }
        .uw-flow.s { animation-duration: 3.6s; }
        .uw-flow.f { animation-duration: 1.9s; }
        @keyframes uwPulse { 0%,100%{ opacity:.4 } 50%{ opacity:1 } }
        .uw-pulse { animation: uwPulse 3.2s ease-in-out infinite; }
        @keyframes uwExt { 0%,42%{ opacity:.85 } 54%,88%{ opacity:.06 } 100%{ opacity:.85 } }
        .uw-ext { animation: uwExt 7s ease-in-out infinite; }
        @keyframes uwExtLbl { 0%,44%{ opacity:0 } 58%,86%{ opacity:1 } 100%{ opacity:0 } }
        .uw-ext-lbl { animation: uwExtLbl 7s ease-in-out infinite; }
        @keyframes uwGlow { 0%,100%{ opacity:.1 } 50%{ opacity:.28 } }
        .uw-glow { animation: uwGlow 3.6s ease-in-out infinite; }
        @keyframes uwRing { 0%{ r:7px; opacity:.6 } 100%{ r:30px; opacity:0 } }
        .uw-ring { animation: uwRing 3s ease-out infinite; }
        @keyframes uwScan { from { transform: translateX(80px); } to { transform: translateX(920px); } }
        .uw-scan { animation: uwScan 8s ease-in-out infinite alternate; }
        @keyframes uwHud { 0%,100%{ opacity:.35 } 50%{ opacity:.7 } }
        .uw-hud { animation: uwHud 4s ease-in-out infinite; }
      `}</style>
      <div className="relative">
        <div aria-hidden className="absolute inset-0 bg-cover bg-center opacity-60" style={{ backgroundImage: `url(${IMG.hero})` }} />
        <div aria-hidden className="absolute inset-0" style={{ background: 'linear-gradient(rgba(8,9,11,0.45), rgba(8,9,11,0.68))' }} />
        <svg viewBox="0 0 1000 680" className="relative w-full h-auto hidden md:block" role="img" aria-label="ULB underground autonomous engineering system schematic">
        <defs>
          <radialGradient id="uwCoreGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={DATACLR} stopOpacity="0.55" />
            <stop offset="100%" stopColor={DATACLR} stopOpacity="0" />
          </radialGradient>
          <radialGradient id="uwHullGlow" cx="50%" cy="45%" r="60%">
            <stop offset="0%" stopColor="#3a4a57" stopOpacity="0.10" />
            <stop offset="100%" stopColor="#3a4a57" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="uwScanGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={AIRCLR} stopOpacity="0" />
            <stop offset="50%" stopColor={AIRCLR} stopOpacity="0.5" />
            <stop offset="100%" stopColor={AIRCLR} stopOpacity="0" />
          </linearGradient>
          <filter id="uwSoft" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="2.4" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <path id="uwSpine" d="M205 212 C255 250 262 274 312 274 S402 232 452 216 S542 290 566 312 S650 272 690 262 S760 322 793 332" fill="none" />
          <path id="uwEnergy" d="M150 548 H830" fill="none" />
          <path id="uwWater" d="M150 572 H830" fill="none" />
          <path id="uwData1" d="M566 396 L452 216" fill="none" />
          <marker id="uwArrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill={GOLD} /></marker>
        </defs>

        {/* HUD frame + backdrop glow */}
        <rect x="60" y="150" width="880" height="360" fill="url(#uwHullGlow)" />
        {[[70,150,1],[930,150,-1],[70,660,1],[930,660,-1]].map(([x,y,dx],i)=>(
          <g key={i} className="uw-hud" stroke={GOLD} strokeWidth="1.5" fill="none" style={{ animationDelay: `${i*0.3}s` }}>
            <path d={`M${x} ${y+ (y<400?18:-18)} L${x} ${y} L${x + dx*18} ${y}`} />
          </g>
        ))}
        <rect x="0" y="150" width="2.5" height="360" fill="url(#uwScanGrad)" className="uw-scan" />

        {/* surface / ground */}
        <text x="34" y="40" fill="#a1a1aa" fontSize="11" letterSpacing="2">EXTERNAL RESOURCES</text>
        <g className="uw-pulse"><circle cx="150" cy="60" r="5" fill="#0a0b0d" stroke={GOLD} strokeWidth="2" /></g>
        <g className="uw-pulse" style={{ animationDelay: '.3s' }}><circle cx="200" cy="60" r="5" fill="#0a0b0d" stroke={WATERCLR} strokeWidth="2" /></g>
        <g className="uw-pulse" style={{ animationDelay: '.6s' }}><circle cx="250" cy="60" r="5" fill="#0a0b0d" stroke={AIRCLR} strokeWidth="2" /></g>
        <line x1="150" y1="66" x2="150" y2="118" stroke={GOLD} strokeWidth="1.5" className="uw-flow" />
        <line x1="200" y1="66" x2="200" y2="118" stroke={WATERCLR} strokeWidth="1.5" className="uw-flow s" />
        <line x1="250" y1="66" x2="250" y2="118" stroke={AIRCLR} strokeWidth="1.5" className="uw-flow f" />

        <text x="34" y="126" fill="#52525b" fontSize="9" letterSpacing="2">GROUND LEVEL</text>
        <line x1="0" y1="120" x2="1000" y2="120" stroke="#2b3640" strokeWidth="1.5" />
        {[...Array(26)].map((_, i) => (<line key={i} x1={40 + i * 38} y1="112" x2={48 + i * 38} y2="120" stroke="#1f2937" strokeWidth="1" />))}

        {/* external network (optional) */}
        <text x="905" y="34" fill="#a1a1aa" fontSize="11" textAnchor="middle" letterSpacing="1">EXTERNAL NETWORK</text>
        <circle cx="905" cy="58" r="7" fill="#0a0b0d" stroke={DATACLR} strokeWidth="2" />
        <text x="905" y="80" fill="#52525b" fontSize="8" textAnchor="middle" letterSpacing="2">OPTIONAL</text>
        <g className="uw-ext">
          <line x1="905" y1="65" x2="905" y2="118" stroke={DATACLR} strokeWidth="1.5" strokeDasharray="3 6" />
          <path d="M905 120 C905 220 800 300 735 396" fill="none" stroke={DATACLR} strokeWidth="1.5" strokeDasharray="3 6" />
        </g>
        <text x="905" y="98" fill="#d9605f" fontSize="8" textAnchor="middle" letterSpacing="1" className="uw-ext-lbl">DISCONNECTED</text>

        {/* entrances */}
        <line x1="196" y1="120" x2="196" y2="196" stroke="#3a4650" strokeWidth="1.5" />
        <line x1="214" y1="120" x2="214" y2="196" stroke="#3a4650" strokeWidth="1.5" />
        <line x1="205" y1="120" x2="205" y2="196" stroke={GOLD} strokeWidth="1.5" className="uw-flow" />
        <text x="205" y="140" fill="#71717a" fontSize="8" textAnchor="middle" letterSpacing="1">SECURE ENTRANCE</text>
        <line x1="784" y1="120" x2="784" y2="300" stroke="#3a4650" strokeWidth="1.5" />
        <line x1="802" y1="120" x2="802" y2="300" stroke="#3a4650" strokeWidth="1.5" />
        <text x="793" y="140" fill="#71717a" fontSize="8" textAnchor="middle" letterSpacing="1">EMERGENCY ACCESS</text>

        {/* reinforced bunker vault (over photo) */}
        <rect x="108" y="172" width="792" height="338" rx="46" fill="rgba(9,11,14,0.42)" stroke="#37454f" strokeWidth="2" />
        <rect x="118" y="182" width="772" height="318" rx="38" fill="none" stroke="#3a4a57" strokeOpacity="0.6" strokeWidth="1" />
        <rect x="108" y="172" width="792" height="338" rx="46" fill="url(#uwHullGlow)" />
        {/* wall reinforcement ticks */}
        {[...Array(25)].map((_, i) => { const x = 150 + i * 30; return (<g key={i} stroke="#2b3742" strokeWidth="1"><line x1={x} y1="172" x2={x} y2="181" /><line x1={x} y1="501" x2={x} y2="510" /></g>); })}
        {/* blast-door hints at entrance shafts */}
        <rect x="195" y="175" width="20" height="8" rx="1" fill="#33414d" />
        <rect x="783" y="175" width="20" height="8" rx="1" fill="#33414d" />

        {/* chambers / rooms (background structure) */}
        <g opacity="0.75" data-testid="uw-structure">
          {[
            { x: 150, y: 188, w: 115, h: 66 },
            { x: 278, y: 250, w: 92, h: 66 },
            { x: 384, y: 182, w: 150, h: 96 },
            { x: 512, y: 284, w: 112, h: 78 },
            { x: 644, y: 232, w: 96, h: 70 },
            { x: 742, y: 300, w: 116, h: 82 },
            { x: 516, y: 372, w: 100, h: 74 },
            { x: 384, y: 320, w: 96, h: 60 },
            { x: 644, y: 322, w: 96, h: 60 },
          ].map((r, i) => (
            <g key={i}>
              <rect x={r.x} y={r.y} width={r.w} height={r.h} rx="3" fill="rgba(14,21,26,0.40)" stroke="#3a4a57" strokeWidth="1" />
              <line x1={r.x + 8} y1={r.y + r.h - 10} x2={r.x + r.w - 8} y2={r.y + r.h - 10} stroke="#26313b" strokeWidth="1" />
              <line x1={r.x + r.w / 2} y1={r.y + 8} x2={r.x + r.w / 2} y2={r.y + r.h - 14} stroke="#26313b" strokeWidth="0.8" />
            </g>
          ))}
          <path d="M265 221 H278 M370 283 H384 M534 230 H548 M624 267 H644 M624 323 H644 M566 362 V372" fill="none" stroke="#33414d" strokeWidth="1.5" />
        </g>

        {/* zone spine */}
        <use href="#uwSpine" stroke="#26313b" strokeWidth="2" />
        <use href="#uwSpine" stroke="#e5e7eb" strokeOpacity="0.6" strokeWidth="1.6" className="uw-flow" />
        {zones.map((z, i) => (
          <g key={i}>
            <circle cx={z.x} cy={z.y} r={z.l === 'RESIDENTIAL CORE' ? 9 : 6} fill="#0c0e11" stroke={z.l === 'RESIDENTIAL CORE' ? AIRCLR : GOLD} strokeWidth="2" className="uw-pulse" style={{ animationDelay: `${i * 0.35}s` }} />
            <text x={z.x} y={z.y - 14} fill="#a1a1aa" fontSize="9.5" textAnchor="middle" letterSpacing="0.5">{z.l}</text>
          </g>
        ))}
        <circle cx="452" cy="216" r="7" fill="none" stroke={AIRCLR} strokeWidth="1.2" className="uw-ring" style={{ animationDelay: '0.7s' }} />
        <text x="452" y="248" fill={AIRCLR} fontSize="8" textAnchor="middle" letterSpacing="1">LIFE SUPPORT · LIVING ENVIRONMENT</text>

        {/* data & knowledge core */}
        <circle cx="566" cy="404" r="16" fill="url(#uwCoreGlow)" />
        <circle cx="566" cy="404" r="6" fill="none" stroke={DATACLR} strokeWidth="1.1" className="uw-ring" />
        <polygon points="566,391 578,398 578,410 566,417 554,410 554,398" fill="#0c0e11" stroke={DATACLR} strokeWidth="1.6" />
        <text x="566" y="434" fill={DATACLR} fontSize="8.5" textAnchor="middle" letterSpacing="1">DATA &amp; KNOWLEDGE CORE</text>
        {dataTargets.map((t, i) => (
          <line key={i} x1="566" y1="404" x2={t.x} y2={t.y} stroke={DATACLR} strokeWidth="1.4" className={`uw-flow ${i % 2 ? 's' : ''}`} />
        ))}
        {dataTargets.filter((t) => t.l !== 'RESIDENTIAL').map((t, i) => (
          <g key={i}>
            <circle cx={t.x} cy={t.y} r="4.5" fill="#0c0e11" stroke={DATACLR} strokeWidth="1.6" />
            <text x={t.x} y={t.y + 18} fill="#71717a" fontSize="8" textAnchor="middle" letterSpacing="1">{t.l}</text>
          </g>
        ))}
        <text x="905" y="430" fill={AIRCLR} fontSize="8" textAnchor="middle" letterSpacing="1">INTERNAL NETWORK</text>
        <text x="905" y="442" fill={AIRCLR} fontSize="8" textAnchor="middle" letterSpacing="1">ACTIVE</text>

        {/* utility gallery (systems bus) */}
        {rails.map((r, i) => (
          <g key={i}>
            <line x1="150" y1={r.y} x2="830" y2={r.y} stroke="#26313b" strokeWidth="2" />
            <line x1="150" y1={r.y} x2="830" y2={r.y} stroke={r.c} strokeWidth="1.6" className={`uw-flow ${i % 2 ? 's' : 'f'}`} />
            <text x="34" y={r.y + 3} fill={r.c} fontSize="10" letterSpacing="1" fontWeight="700">{r.l}</text>
            <text x="150" y={r.y - 8} fill="#5b616b" fontSize="7.5" letterSpacing="1">{r.s}</text>
            {[300, 500, 700].map((x, j) => (<circle key={j} cx={x} cy={r.y} r="3.5" fill="#0a0b0d" stroke={r.c} strokeWidth="1.4" />))}
          </g>
        ))}
        {/* risers from gallery into hull */}
        {[320, 520, 700].map((x, i) => (
          <line key={i} x1={x} y1="500" x2={x} y2="540" stroke="#e5e7eb" strokeOpacity="0.5" strokeWidth="1.4" className="uw-flow" strokeDasharray="4 10" />
        ))}
        {/* W2E loop: waste recovery -> energy */}
        <path d="M800 620 C862 606 866 560 806 548" fill="none" stroke={WASTECLR} strokeWidth="1.6" className="uw-flow" markerEnd="url(#uwArrow)" />
        <text x="872" y="588" fill={WASTECLR} fontSize="8" textAnchor="middle" letterSpacing="1">W2E</text>

        {/* travelling particles */}
        <circle r="3.6" fill={GOLD} filter="url(#uwSoft)"><animateMotion dur="3.2s" repeatCount="indefinite"><mpath href="#uwEnergy" /></animateMotion></circle>
        <circle r="3.6" fill={WATERCLR} filter="url(#uwSoft)"><animateMotion dur="4.2s" repeatCount="indefinite"><mpath href="#uwWater" /></animateMotion></circle>
        <circle r="3.4" fill={DATACLR} filter="url(#uwSoft)"><animateMotion dur="3.6s" repeatCount="indefinite"><mpath href="#uwData1" /></animateMotion></circle>
        <circle r="3.4" fill="#ffffff" opacity="0.85" filter="url(#uwSoft)"><animateMotion dur="5s" repeatCount="indefinite"><mpath href="#uwSpine" /></animateMotion></circle>
      </svg>

      {/* MOBILE — vertical schematic */}
      <svg viewBox="0 0 420 800" className="relative w-full h-auto md:hidden" role="img" aria-label="ULB underground system schematic (mobile)">
        <defs>
          <path id="uwSpineM" d="M150 150 C210 185 215 195 270 228 S150 272 150 306 S270 352 270 386 S150 436 150 470 S270 516 270 550" fill="none" />
          <radialGradient id="uwCoreGlowM" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={DATACLR} stopOpacity="0.55" />
            <stop offset="100%" stopColor={DATACLR} stopOpacity="0" />
          </radialGradient>
        </defs>

        <text x="16" y="24" fill="#a1a1aa" fontSize="10" letterSpacing="1">EXTERNAL RESOURCES</text>
        {[[70, GOLD], [100, WATERCLR], [130, AIRCLR]].map(([x, c], i) => (
          <g key={i}><circle cx={x} cy="44" r="4" fill="#0a0b0d" stroke={c} strokeWidth="1.6" className="uw-pulse" style={{ animationDelay: `${i * 0.3}s` }} /><line x1={x} y1="49" x2={x} y2="66" stroke={c} strokeWidth="1.3" className="uw-flow" /></g>
        ))}
        <text x="404" y="24" fill="#a1a1aa" fontSize="10" textAnchor="end" letterSpacing="1">EXT. NETWORK</text>
        <circle cx="360" cy="44" r="5" fill="#0a0b0d" stroke={DATACLR} strokeWidth="1.6" />
        <g className="uw-ext"><line x1="360" y1="49" x2="360" y2="66" stroke={DATACLR} strokeWidth="1.3" strokeDasharray="3 5" /><path d="M360 68 C360 140 300 200 270 228" fill="none" stroke={DATACLR} strokeWidth="1.3" strokeDasharray="3 5" /></g>
        <text x="360" y="84" fill="#d9605f" fontSize="7" textAnchor="middle" className="uw-ext-lbl">DISCONNECTED</text>

        <line x1="0" y1="68" x2="420" y2="68" stroke="#2b3640" strokeWidth="1.5" />
        <text x="404" y="62" fill="#52525b" fontSize="8" textAnchor="end">GROUND LEVEL</text>

        <rect x="24" y="92" width="372" height="662" rx="28" fill="rgba(9,11,14,0.40)" stroke="#37454f" strokeWidth="1.8" />
        <line x1="100" y1="68" x2="100" y2="150" stroke="#3a4650" strokeWidth="1.4" />
        <text x="100" y="82" fill="#71717a" fontSize="7" textAnchor="middle">SECURE ENTRANCE</text>

        {zonesM.map((z, i) => (<rect key={i} x={z.x - 46} y={z.y - 24} width="92" height="48" rx="3" fill="rgba(14,21,26,0.4)" stroke="#3a4a57" strokeWidth="0.8" />))}

        <use href="#uwSpineM" xlinkHref="#uwSpineM" stroke="#26313b" strokeWidth="2" />
        <use href="#uwSpineM" xlinkHref="#uwSpineM" stroke="#e5e7eb" strokeOpacity="0.6" strokeWidth="1.5" className="uw-flow" />
        {zonesM.map((z, i) => (
          <g key={i}>
            <circle cx={z.x} cy={z.y} r={z.big ? 8 : 6} fill="#0c0e11" stroke={z.big ? AIRCLR : GOLD} strokeWidth="2" className="uw-pulse" style={{ animationDelay: `${i * 0.3}s` }} />
            <text x={z.x} y={z.y - 14} fill="#cbd5e1" fontSize="9" textAnchor="middle">{z.l}</text>
          </g>
        ))}

        <circle cx="200" cy="452" r="14" fill="url(#uwCoreGlowM)" />
        <circle cx="200" cy="452" r="6" fill="none" stroke={DATACLR} strokeWidth="1">
          <animate attributeName="r" values="6;22;6" dur="3s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.7;0;0.7" dur="3s" repeatCount="indefinite" />
        </circle>
        <polygon points="200,441 210,447 210,457 200,463 190,457 190,447" fill="#0c0e11" stroke={DATACLR} strokeWidth="1.4" />
        <text x="200" y="480" fill={DATACLR} fontSize="8" textAnchor="middle">DATA &amp; KNOWLEDGE CORE</text>
        {[[110, 430, 'EDU'], [300, 486, 'COMMS']].map(([x, y, l], i) => (
          <g key={i}><line x1="200" y1="452" x2={x} y2={y} stroke={DATACLR} strokeWidth="1.2" className={`uw-flow ${i ? 's' : ''}`} /><circle cx={x} cy={y} r="4" fill="#0c0e11" stroke={DATACLR} strokeWidth="1.3" /><text x={x} y={y + 14} fill="#71717a" fontSize="7" textAnchor="middle">{l}</text></g>
        ))}

        {[140, 280].map((x, i) => (<line key={i} x1={x} y1="580" x2={x} y2="632" stroke="#e5e7eb" strokeOpacity="0.5" strokeWidth="1.3" strokeDasharray="4 8" className="uw-flow" />))}
        {railsM.map((r, i) => (
          <g key={i}>
            <line x1="60" y1={r.y} x2="360" y2={r.y} stroke="#26313b" strokeWidth="1.8" />
            <line x1="60" y1={r.y} x2="360" y2={r.y} stroke={r.c} strokeWidth="1.4" className={`uw-flow ${i % 2 ? 's' : 'f'}`} />
            <text x="16" y={r.y + 3} fill={r.c} fontSize="8" fontWeight="700">{r.l}</text>
            {[120, 210, 300].map((x, j) => (<circle key={j} cx={x} cy={r.y} r="3" fill="#0a0b0d" stroke={r.c} strokeWidth="1.2" />))}
          </g>
        ))}
        <path d="M340 722 C382 710 384 655 348 632" fill="none" stroke={WASTECLR} strokeWidth="1.4" className="uw-flow" />
        <text x="374" y="678" fill={WASTECLR} fontSize="7" textAnchor="middle">W2E</text>
        <text x="210" y="742" fill={AIRCLR} fontSize="8" textAnchor="middle" letterSpacing="1">INTERNAL NETWORK ACTIVE</text>

        <circle r="3" fill="#ffffff" opacity="0.85"><animateMotion dur="5s" repeatCount="indefinite" path="M150 150 C210 185 215 195 270 228 S150 272 150 306 S270 352 270 386 S150 436 150 470 S270 516 270 550" /></circle>
        <circle r="3" fill={GOLD}><animateMotion dur="3.4s" repeatCount="indefinite" path="M60 632 H360" /></circle>
        <circle r="3" fill={DATACLR}><animateMotion dur="3.6s" repeatCount="indefinite" path="M200 452 L150 306" /></circle>
      </svg>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2">
        {legend.map((x, i) => (
          <span key={i} className="inline-flex items-center gap-2 text-[11px] text-zinc-500">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: x.c }} />{x.l}
          </span>
        ))}
      </div>
      <p className="mt-5 text-sm text-zinc-400 leading-relaxed max-w-3xl">One underground structure — multiple interconnected systems engineered as a single autonomous environment for continuity, communication, education and knowledge. The internal data and knowledge network remains active independently of external connectivity.</p>
      <p className="mt-3 text-xs text-zinc-600">A comprehensive offline archive of human knowledge, education and cultural resources — science, medicine, engineering, agriculture, history, literature, languages and technical references. Illustrative conceptual schematic; every ULB is individually engineered.</p>
    </div>
  );
};

export default function LuxuryBunkers({ lang, setLang }) {
  useReveal(1);
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-[#0a0b0d] text-zinc-100 font-['Space_Grotesk',sans-serif]" data-testid="luxury-bunkers-page">
      <SiteHeader lang={lang} setLang={setLang} />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-20 md:bg-fixed" style={{ backgroundImage: `linear-gradient(to right, rgba(8,9,11,0.72), rgba(8,9,11,0.30)), linear-gradient(to top, rgba(8,9,11,0.75), rgba(8,9,11,0.05)), url(${IMG.hero})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full hero-content">
          <Kicker>A specialized engineering program by SYNERGOATOM SE</Kicker>
          <h1 className="text-6xl sm:text-8xl lg:text-9xl font-bold tracking-tight font-['Syne',sans-serif] leading-[0.9]">ULB</h1>
          <p className="mt-4 text-2xl sm:text-4xl font-bold text-zinc-100 tracking-wide font-['Syne',sans-serif]">ULTIMATE LUXURY BUNKERS</p>
          <p className="mt-6 text-lg sm:text-2xl text-zinc-300 tracking-wide font-['Syne',sans-serif]">PRIVATE PROTECTIVE INFRASTRUCTURE</p>
          <p className="mt-5 max-w-2xl text-zinc-400 text-base sm:text-lg leading-relaxed">Advanced private underground environments engineered by SYNERGOATOM SE — combining protective engineering, autonomous infrastructure and uncompromised architecture.</p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <button onClick={() => scrollTo('brief')} data-testid="hero-consultation-cta" className="px-8 py-4 font-semibold rounded-sm text-[#0a0b0d] transition-transform hover:-translate-y-0.5 inline-flex items-center justify-center gap-2" style={{ backgroundColor: GOLD }}>REQUEST A CONFIDENTIAL CONSULTATION<ArrowRight className="w-4 h-4" /></button>
            <button onClick={() => scrollTo('technology')} className="px-8 py-4 font-semibold rounded-sm border border-zinc-600 text-zinc-200 hover:border-zinc-300 transition-colors inline-flex items-center justify-center gap-2">EXPLORE THE TECHNOLOGY<ArrowDown className="w-4 h-4" /></button>
          </div>
          <p className="mt-14 text-xs text-zinc-500 tracking-widest uppercase flex items-center gap-3"><span className="inline-block w-10 h-px bg-zinc-600" />Protection + Architecture</p>
        </div>
      </section>

      {/* ENGINEERING DNA */}
      <Section id="technology" className="py-20 sm:py-28 border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Kicker>Engineering DNA</Kicker>
          <h2 className="text-3xl sm:text-5xl font-bold font-['Syne',sans-serif] max-w-4xl leading-tight">FROM CRITICAL INFRASTRUCTURE TO PRIVATE PROTECTION</h2>
          <p className="mt-6 max-w-2xl text-zinc-400 text-lg">Engineering developed for some of the world's most demanding infrastructure environments — now applied to private secure architecture.</p>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-px bg-zinc-900 border border-zinc-900">
            {dna.map((d, i) => (
              <div key={i} className="bg-[#0a0b0d] p-6 flex flex-col gap-4 hover:bg-[#101317] transition-colors">
                <d.icon className="w-6 h-6" style={{ color: GOLD }} />
                <span className="text-sm text-zinc-300 uppercase tracking-wide">{d.t}</span>
              </div>
            ))}
          </div>
          <p className="mt-10 text-zinc-500 max-w-3xl">ULB — Ultimate Luxury Bunkers is not the beginning of our protective engineering expertise. It is one of its applications.</p>
        </div>
      </Section>

      {/* ECOSYSTEM */}
      <Section className="py-20 sm:py-28 bg-[#0c0e11] border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Kicker>The Ecosystem</Kicker>
            <h2 className="text-3xl sm:text-4xl font-bold font-['Syne',sans-serif] leading-tight">An engineering ecosystem normally reserved for strategic infrastructure</h2>
            <p className="mt-6 text-zinc-400 text-lg leading-relaxed">SYNERGOATOM SE operates as the system integrator of <a href="https://triumph-alliance.com" target="_blank" rel="noopener noreferrer" className="underline decoration-zinc-600 hover:text-white" style={{ color: GOLD }}>TRIUMPH Alliance</a> — an international strategic grouping connecting capabilities across nuclear energy, industrial engineering, defence, infrastructure, construction, advanced materials and R&amp;D.</p>
            <p className="mt-4 text-zinc-500">Through this ecosystem, a private client accesses protective engineering culture built for critical infrastructure.</p>
          </div>
          <div className="grid grid-cols-2 gap-px bg-zinc-900 border border-zinc-900">
            {['Nuclear Energy', 'Industrial Engineering', 'Defence', 'Infrastructure', 'Construction', 'Advanced Materials & R&D'].map((x, i) => (
              <div key={i} className="bg-[#0a0b0d] p-6 text-zinc-300 text-sm uppercase tracking-wide">{x}</div>
            ))}
          </div>
        </div>
      </Section>

      {/* ENGINEERING & INDUSTRIAL ECOSYSTEM */}
      <Section className="py-20 sm:py-28 border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Kicker>The engineering environment</Kicker>
          <h2 className="text-3xl sm:text-4xl font-bold font-['Syne',sans-serif]">ENGINEERING &amp; INDUSTRIAL ECOSYSTEM</h2>
          <p className="mt-6 max-w-3xl text-zinc-400 text-lg">The organizations whose engineering, industrial and scientific capabilities form the environment behind the program. SYNERGOATOM SE works with leading European scientific and technical organizations in the development of defence technologies for critical infrastructure.</p>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900">
            {partners.map((p, i) => (
              <a key={p.name} href={p.url} target="_blank" rel="noopener noreferrer" aria-label={`Visit ${p.name} official website`} data-testid={`eco-logo-${i}`} className="group relative bg-[#0a0b0d] flex items-center justify-center px-6 py-12 hover:bg-[#12151a] transition-colors">
                {p.text ? (
                  <span className="text-center leading-tight opacity-70 group-hover:opacity-100 group-hover:scale-[1.03] transition-all duration-300">
                    <span className="block text-xl font-bold font-['Syne',sans-serif] text-white tracking-wide">RIZZO</span>
                    <span className="block text-[10px] tracking-[0.3em] text-white/80 mt-1">ASSOCIATES</span>
                  </span>
                ) : (
                  <img src={p.src} alt={p.name} loading="lazy" className={`w-auto max-w-full object-contain transition-all duration-300 group-hover:scale-[1.03] group-hover:opacity-100 ${p.boost ? 'opacity-90' : 'opacity-70'} ${p.shrink ? 'max-h-10 sm:max-h-12' : 'max-h-12 sm:max-h-16'}`} />
                )}
              </a>
            ))}
          </div>
        </div>
      </Section>

      {/* W-FORTEX */}
      <Section className="relative py-24 sm:py-32 border-t border-zinc-900 md:bg-fixed" style={{ backgroundImage: `linear-gradient(rgba(8,9,11,0.8), rgba(8,9,11,0.92)), url(${IMG.wfortex})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <img src="/logos/wfortex.png" alt="W-FORTEX" className="h-28 sm:h-36 w-auto mb-8 opacity-90 drop-shadow-lg" />
          <Kicker>Physical Protection of Critical Infrastructure</Kicker>
          <h2 className="text-4xl sm:text-6xl font-bold font-['Syne',sans-serif] tracking-tight">W-FORTEX</h2>
          <p className="mt-6 max-w-2xl text-zinc-300 text-lg">A protective infrastructure program connected to SYNERGOATOM's capabilities for the physical protection of strategic assets.</p>
          <a href="https://triumph-alliance.com/programs/w-fortex" target="_blank" rel="noopener noreferrer" data-testid="wfortex-link" className="mt-5 inline-flex items-center gap-2 text-sm font-medium border-b border-transparent hover:border-current pb-0.5 transition-colors" style={{ color: GOLD }}>View the W-FORTEX program<ArrowRight className="w-4 h-4" /></a>
          <div className="mt-12 flex flex-wrap items-center gap-3 text-sm text-zinc-400">
            {['Critical Infrastructure', 'Protective Shell', 'Underground Architecture', 'Private Residence'].map((x, i) => (
              <React.Fragment key={i}>
                <span className="px-4 py-2 border border-zinc-700 rounded-sm bg-[#0a0b0d]/60">{x}</span>
                {i < 3 && <ChevronRight className="w-4 h-4 text-zinc-600" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </Section>

      {/* IBIPC PROTECTIVE CONCRETE */}
      <Section id="material" className="py-20 sm:py-28 border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap lg:flex-nowrap items-center gap-6 sm:gap-10 mb-12" data-testid="ibipc-logo-row">
            <a href="https://www.ibipc.com/" target="_blank" rel="noopener noreferrer" aria-label="Visit IBIPC official website" data-testid="ibipc-section-logo" className="inline-flex items-center shrink-0 opacity-95 hover:opacity-100 transition-opacity">
              <img src="/logos/ibipc_color.png" alt="IBIPC — Institute of Blast & Impact Proof Concrete" className="h-12 sm:h-16 w-auto" />
            </a>
            <a href="https://www.ibipc.com/" target="_blank" rel="noopener noreferrer" aria-label="IBIPC STANAG qualification" data-testid="ibipc-stanag" className="inline-flex items-center gap-3 shrink-0 bg-white rounded-sm px-3 py-2 opacity-95 hover:opacity-100 transition-opacity">
              <img src="/logos/stanag.png" alt="STANAG 2280:2016 — IBIPC NCAGE Code 8286G" className="h-10 w-auto" />
              <span className="text-[10px] leading-snug text-zinc-700">STANAG 2280:2016<br /><span className="text-zinc-500">NCAGE 8286G</span></span>
            </a>
            <a href="https://www.vvu.cz/" target="_blank" rel="noopener noreferrer" aria-label="Visit VVÚ official website" data-testid="ibipc-vvu" className="inline-flex items-center shrink-0">
              <img src="/logos/vvu.png" alt="VVÚ" className="h-7 sm:h-9 w-auto opacity-80 hover:opacity-100 transition-opacity" />
            </a>
            <a href="https://acr.mo.gov.cz/" target="_blank" rel="noopener noreferrer" aria-label="Visit Army of the Czech Republic official website" data-testid="ibipc-acr" className="inline-flex items-center shrink-0">
              <img src="/logos/acr.png" alt="Armáda České republiky" className="h-10 sm:h-12 w-auto opacity-90 hover:opacity-100 transition-opacity" />
            </a>
          </div>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <Kicker>The Protective Shell</Kicker>
            <h2 className="text-3xl sm:text-4xl font-bold font-['Syne',sans-serif]">IBIPC — Advanced Protective Concrete</h2>
            <p className="mt-6 text-zinc-400 text-lg leading-relaxed">A high-performance protective concrete technology within the protective engineering ecosystem — engineering documentation expressed as architecture.</p>
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
              {[
                { v: 165, d: 0, s: '', l: 'MPa · Compressive' },
                { v: 15, d: 0, s: '', l: 'MPa · Tensile' },
                { v: 50, d: 0, s: '', l: 'MPa · Flexural (45–50)' },
                { v: 1, d: 0, s: '%', l: 'Water absorption (<1%)' },
                { v: 2500, d: 0, s: '', l: 'kg/m³ · Density' },
              ].map((m, i) => (
                <div key={i} className="bg-[#0a0b0d] p-6">
                  <div className="text-3xl font-bold font-['Syne',sans-serif]" style={{ color: GOLD }}><Counter value={m.v} decimals={m.d} suffix={m.s} /></div>
                  <div className="mt-2 text-[11px] text-zinc-500 uppercase tracking-wide">{m.l}</div>
                </div>
              ))}
            </div>
            <p className="mt-6 text-xs text-zinc-600 leading-relaxed">Parameters reflect the protective concrete technology specification. Protective performance, testing and any qualification against defence-related standards are confirmed per project through applicable testing and documentation.</p>
          </div>
          <div className="order-1 lg:order-2">
            <img src={IMG.concrete} alt="Advanced protective concrete macro" loading="lazy" className="w-full h-auto rounded-sm border border-zinc-800" />
          </div>
          </div>
        </div>
      </Section>

      {/* PROTECTION AT ANY SCALE */}
      <Section id="scalable" className="relative py-20 sm:py-28 border-t border-zinc-900 bg-[#0c0e11] overflow-hidden">
        <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)', backgroundSize: '52px 52px' }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="scalable-section">
          <Kicker>Scalable protective engineering</Kicker>
          <h2 className="text-3xl sm:text-4xl font-bold font-['Syne',sans-serif] leading-tight max-w-4xl">PROTECTION AT ANY SCALE</h2>
          <div className="mt-6 max-w-3xl space-y-4 text-zinc-400 text-lg leading-relaxed">
            <p>ULB is not only large bespoke underground residences of 500, 1,000+ m². The same philosophy of advanced protection can be applied through more compact, scalable and modular solutions.</p>
          </div>
          <p className="mt-8 text-2xl sm:text-3xl font-bold font-['Syne',sans-serif] leading-tight max-w-4xl" style={{ color: GOLD }}>The level of engineering does not have to depend on the size of the structure.</p>
          <p className="mt-6 max-w-3xl text-zinc-400 leading-relaxed">Alongside fully bespoke large-scale ULB projects, SYNERGOATOM SE can develop compact protective modules and modular bunker configurations based on the same protective-engineering philosophy and, where appropriate, incorporating IBIPC advanced protective concrete technology.</p>

          {/* Modular configurations */}
          <div className="mt-14">
            <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-6">Modular ULB — configurations</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900">
              {[
                { icon: ShieldCheck, t: 'Compact Stand-Alone ULB', d: 'A complete protected environment within a smaller footprint.' },
                { icon: Boxes, t: 'Modular ULB', d: 'Several protected modules combined into a larger functional system.' },
                { icon: Maximize2, t: 'Expandable ULB', d: 'Conceived so additional protected modules and zones may be integrated over time where feasible.' },
                { icon: Network, t: 'Distributed Protection', d: 'Multiple protected elements incorporated into a larger private estate or infrastructure concept.' },
              ].map((c, i) => (
                <div key={i} className="bg-[#0a0b0d] p-6" data-testid={`scale-config-${i}`}>
                  <c.icon className="w-6 h-6 mb-4" style={{ color: GOLD }} />
                  <h3 className="text-base font-semibold text-zinc-100 font-['Syne',sans-serif]">{c.t}</h3>
                  <p className="mt-2 text-sm text-zinc-500 leading-relaxed">{c.d}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-zinc-500 leading-relaxed max-w-3xl">The objective is not a “budget bunker” — it is to make advanced protective engineering scalable. A smaller ULB remains sophisticated, highly engineered and individually configured, while requiring a different construction, logistics and integration strategy.</p>
          </div>

          {/* Visual references — compact module & multi-module system */}
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="scale-visual-slots">
            {[
              { src: 'https://customer-assets-39nsmqrw.emergentagent.net/job_synergo-atom/artifacts/kpflh2bl_image.png', t: 'Compact Stand-Alone ULB', d: 'A complete protected environment within a smaller footprint.' },
              { src: 'https://customer-assets-39nsmqrw.emergentagent.net/job_synergo-atom/artifacts/6wynrc8r_image.png', t: 'Multi-Module ULB System', d: 'Several protected modules combined into a larger functional system.' },
            ].map((v, i) => (
              <figure key={i} className="relative overflow-hidden rounded-sm border border-zinc-800 group" data-testid={`scale-visual-${i}`}>
                <img src={v.src} alt={v.t} loading="lazy" className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.02]" />
                <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(135deg, rgba(8,9,11,0.32), rgba(8,9,11,0.5))' }} />
                <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 400 260" preserveAspectRatio="none" aria-hidden="true">
                  {[80, 160, 240, 320].map((x, k) => (<line key={k} x1={x} y1="0" x2={x} y2="260" stroke="rgba(143,176,189,0.28)" strokeWidth="0.5" />))}
                  {[85, 170].map((y, k) => (<line key={k} x1="0" y1={y} x2="400" y2={y} stroke="rgba(143,176,189,0.28)" strokeWidth="0.5" />))}
                  {i === 0 ? (
                    <g>
                      <line x1="40" y1="34" x2="180" y2="34" stroke="rgba(212,168,75,0.6)" strokeWidth="0.8" /><line x1="40" y1="28" x2="40" y2="40" stroke="rgba(212,168,75,0.6)" strokeWidth="0.8" /><line x1="180" y1="28" x2="180" y2="40" stroke="rgba(212,168,75,0.6)" strokeWidth="0.8" />
                      <text x="110" y="28" fill="rgba(212,168,75,0.8)" fontSize="7" textAnchor="middle" letterSpacing="1.5">SECTION A–A</text>
                      <circle cx="300" cy="120" r="13" fill="none" stroke="rgba(212,168,75,0.6)" strokeWidth="0.8" /><line x1="281" y1="120" x2="319" y2="120" stroke="rgba(212,168,75,0.6)" strokeWidth="0.7" /><line x1="300" y1="101" x2="300" y2="139" stroke="rgba(212,168,75,0.6)" strokeWidth="0.7" />
                      <path d="M16 16 H44 M16 16 V40" fill="none" stroke="rgba(212,168,75,0.6)" strokeWidth="1" /><path d="M384 244 H356 M384 244 V220" fill="none" stroke="rgba(212,168,75,0.6)" strokeWidth="1" />
                    </g>
                  ) : (
                    <g>
                      <line x1="40" y1="60" x2="40" y2="190" stroke="rgba(212,168,75,0.6)" strokeWidth="0.8" /><line x1="34" y1="60" x2="46" y2="60" stroke="rgba(212,168,75,0.6)" strokeWidth="0.8" /><line x1="34" y1="190" x2="46" y2="190" stroke="rgba(212,168,75,0.6)" strokeWidth="0.8" />
                      <text x="54" y="128" fill="rgba(212,168,75,0.8)" fontSize="7" letterSpacing="1.5">MODULE ×3</text>
                      <circle cx="110" cy="120" r="13" fill="none" stroke="rgba(212,168,75,0.6)" strokeWidth="0.8" /><line x1="91" y1="120" x2="129" y2="120" stroke="rgba(212,168,75,0.6)" strokeWidth="0.7" /><line x1="110" y1="101" x2="110" y2="139" stroke="rgba(212,168,75,0.6)" strokeWidth="0.7" />
                      <path d="M356 16 H384 M384 16 V40" fill="none" stroke="rgba(212,168,75,0.6)" strokeWidth="1" /><path d="M16 244 H44 M16 244 V220" fill="none" stroke="rgba(212,168,75,0.6)" strokeWidth="1" />
                      <path d="M240 210 H360 M246 206 L240 210 L246 214 M354 206 L360 210 L354 214" fill="none" stroke="rgba(143,176,189,0.5)" strokeWidth="0.7" />
                    </g>
                  )}
                </svg>
                <figcaption className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-[#08090b] via-[#08090b]/70 to-transparent">
                  <span className="text-sm font-semibold text-zinc-100 font-['Syne',sans-serif]">{v.t}</span>
                  <span className="block text-xs text-zinc-400 mt-0.5">{v.d}</span>
                </figcaption>
              </figure>
            ))}
          </div>
          <p className="mt-3 text-xs text-zinc-600">Progression: compact module → multi-module system → large bespoke ULB.</p>

          {/* Factory -> Logistics -> Local integration */}
          <div className="mt-16 border-t border-zinc-800 pt-14">
            <Kicker>Global technology. Local execution.</Kicker>
            <h3 className="text-2xl sm:text-3xl font-bold font-['Syne',sans-serif] leading-tight max-w-3xl">FACTORY → LOGISTICS → LOCAL INTEGRATION</h3>
            <p className="mt-6 max-w-3xl text-zinc-400 leading-relaxed">Where technically appropriate, selected structural and protective elements may be produced and prepared off-site and transported internationally for final installation.</p>

            <div className="mt-10 flex flex-wrap items-center gap-x-2 gap-y-4">
              {[
                { icon: Cpu, t: 'Engineering' },
                { icon: Factory, t: 'Prefabrication' },
                { icon: Truck, t: 'Transport' },
                { icon: MapPin, t: 'Site Integration' },
                { icon: PackageCheck, t: 'Commissioning' },
              ].map((s, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <ArrowRight className="w-4 h-4 text-zinc-600 shrink-0" />}
                  <span className="inline-flex items-center gap-2 px-4 py-2.5 border border-zinc-800 rounded-sm bg-[#0c0e11]">
                    <s.icon className="w-4 h-4" style={{ color: GOLD }} />
                    <span className="text-xs sm:text-sm uppercase tracking-wide text-zinc-200">{s.t}</span>
                  </span>
                </React.Fragment>
              ))}
            </div>

            <p className="mt-12 text-2xl sm:text-3xl font-bold font-['Syne',sans-serif] leading-tight">
              <span className="block text-white">Global technology.</span>
              <span className="block" style={{ color: GOLD }}>Local execution.</span>
            </p>
            <div className="mt-6 max-w-3xl space-y-4 text-zinc-400 text-lg leading-relaxed">
              <p>Complex underground construction cannot be separated from local conditions. Large-scale excavation, foundations, structural integration, utilities, access works and heavy installation require qualified, verified and reliable local engineering and construction partners.</p>
              <p>For international ULB projects, SYNERGOATOM SE combines its central engineering, protective technology and project integration capabilities with carefully selected local contractors and specialists where required.</p>
            </div>

            <div className="mt-8 flex flex-wrap gap-2">
              {['Local Geology', 'Site Conditions', 'Construction Practice', 'Regulation', 'Logistics', 'Permitting', 'Local Engineering'].map((c) => (
                <span key={c} className="px-3.5 py-1.5 text-xs rounded-sm border border-zinc-800 text-zinc-300" style={{ backgroundColor: '#0c0e11' }}>{c}</span>
              ))}
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {['Central Engineering', 'Advanced Protective Technology', 'Industrial Prefabrication', 'Verified Local Execution'].map((x, i) => (
                <div key={i} className="flex items-center gap-3 border border-zinc-800 rounded-sm bg-[#0a0b0d] p-4" data-testid={`delivery-model-${i}`}>
                  <Check className="w-4 h-4 shrink-0" style={{ color: GOLD }} />
                  <span className="text-sm text-zinc-200 leading-snug">{x}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Core message */}
          <div className="mt-16 border-t border-zinc-800 pt-12">
            <p className="text-2xl sm:text-3xl md:text-4xl font-bold font-['Syne',sans-serif] leading-tight" style={{ color: GOLD }}>Protection can grow with the project.</p>
            <p className="mt-6 max-w-3xl text-zinc-400 text-lg leading-relaxed">From a compact autonomous protected module to a major underground private infrastructure complex, ULB is conceived as a scalable engineering system — not a single predetermined bunker format.</p>
          </div>
        </div>
      </Section>

      {/* WITKOWITZ — INDUSTRIAL POWER */}
      <Section id="witkowitz" className="py-20 sm:py-28 border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="witkowitz-section">
          <a href="https://witkowitz.eu/" target="_blank" rel="noopener noreferrer" aria-label="Visit WITKOWITZ official website" data-testid="witkowitz-logo" className="inline-flex items-center mb-10 opacity-90 hover:opacity-100 transition-opacity">
            <img src="/logos/witkowitz.png" alt="WITKOWITZ" className="h-10 sm:h-14 w-auto" />
          </a>
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <Kicker>Industrial power behind ULB</Kicker>
              <h2 className="text-3xl sm:text-4xl font-bold font-['Syne',sans-serif] leading-tight">WITKOWITZ GROUP</h2>
              <div className="mt-6 space-y-4 text-zinc-400 text-lg leading-relaxed">
                <p>ULB is supported by the industrial and engineering capabilities of WITKOWITZ — one of Central Europe’s major industrial engineering groups, building on approximately two centuries of Czech industrial and engineering tradition.</p>
                <p>WITKOWITZ represents substantial engineering, manufacturing and heavy-industry capabilities, including the production of sophisticated technological equipment, complex steel structures and components for demanding industrial applications.</p>
                <p>Its competencies extend into highly demanding sectors including nuclear energy, where companies within the group participate in the manufacture and engineering of equipment and components for nuclear power facilities.</p>
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-5">Industrial competencies</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900">
                {[
                  { icon: Factory, t: 'Heavy Engineering' },
                  { icon: Cpu, t: 'Advanced Manufacturing' },
                  { icon: Atom, t: 'Nuclear Industry Experience' },
                  { icon: Layers, t: 'Complex Steel Structures' },
                  { icon: PackageCheck, t: 'Industrial Project Delivery' },
                  { icon: ShieldCheck, t: 'Engineered for ULB' },
                ].map((c, i) => (
                  <div key={i} className="bg-[#0a0b0d] p-5 flex items-center gap-3" data-testid={`witkowitz-cap-${i}`}>
                    <c.icon className="w-5 h-5 shrink-0" style={{ color: GOLD }} />
                    <span className="text-sm text-zinc-200 leading-snug">{c.t}</span>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-sm text-zinc-500 leading-relaxed">For ULB, this means access not only to design and protective technologies, but also to the real industrial capabilities required to manufacture and deliver complex engineered structures and technological systems.</p>
            </div>
          </div>

          {/* GLOBAL EXPERIENCE — reserved map area */}
          <div className="mt-16 border-t border-zinc-800 pt-14">
            <Kicker>Global experience</Kicker>
            <p className="max-w-3xl text-zinc-400 text-lg leading-relaxed">WITKOWITZ and its industrial tradition are supported by international project experience and references across global markets.</p>
            <div className="mt-8 relative w-full" data-testid="witkowitz-map-slot">
              <img src="https://customer-assets-39nsmqrw.emergentagent.net/job_synergo-atom/artifacts/fkz0lukf_image.png" alt="WITKOWITZ global reference map" loading="lazy" className="w-full h-auto block mix-blend-screen opacity-90" />
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1000 455" preserveAspectRatio="none" aria-hidden="true">
                <style>{`@keyframes wkFlow{to{stroke-dashoffset:-60}}.wk-flow{stroke-dasharray:3 9;animation:wkFlow 2.6s linear infinite}.wk-flow.s{animation-duration:3.6s}@keyframes wkP{0%,100%{opacity:.4}50%{opacity:1}}.wk-p{animation:wkP 3s ease-in-out infinite}`}</style>
                {[[120, 230], [90, 300], [230, 410], [300, 320], [430, 300], [560, 235], [600, 330], [665, 300], [715, 285], [830, 405], [895, 265], [940, 310]].map(([nx, ny], i) => {
                  const hx = 475, hy = 172; const mx = (hx + nx) / 2; const my = Math.min(hy, ny) - 45;
                  const d = `M${hx} ${hy} Q${mx} ${my} ${nx} ${ny}`;
                  return (
                    <g key={i}>
                      <path d={d} fill="none" stroke="rgba(143,176,189,0.30)" strokeWidth="1" />
                      <path d={d} fill="none" stroke="rgba(212,168,75,0.85)" strokeWidth="1.1" className={`wk-flow ${i % 2 ? 's' : ''}`} />
                      <circle cx={nx} cy={ny} r="3" fill="#0a0b0d" stroke="rgba(212,168,75,0.9)" strokeWidth="1.2" className="wk-p" style={{ animationDelay: `${i * 0.25}s` }} />
                      {i % 4 === 0 && <circle r="2.4" fill="#D4A84B"><animateMotion dur={`${3 + i * 0.4}s`} repeatCount="indefinite" path={d} /></circle>}
                    </g>
                  );
                })}
                <circle cx="475" cy="172" r="5" fill="#0a0b0d" stroke="#D4A84B" strokeWidth="1.6" />
                <circle cx="475" cy="172" r="5" fill="none" stroke="#D4A84B" strokeWidth="1.2"><animate attributeName="r" values="5;16;5" dur="3s" repeatCount="indefinite" /><animate attributeName="opacity" values="0.8;0;0.8" dur="3s" repeatCount="indefinite" /></circle>
              </svg>
              <span className="absolute bottom-3 right-4 text-[10px] tracking-[0.25em] text-zinc-500 uppercase">Global reference network</span>
            </div>
          </div>

          {/* Core message */}
          <div className="mt-16 border-t border-zinc-800 pt-12">
            <p className="text-2xl sm:text-3xl md:text-4xl font-bold font-['Syne',sans-serif] leading-tight" style={{ color: GOLD }}>ULB is backed by real industrial capability.</p>
            <p className="mt-6 max-w-3xl text-zinc-400 text-lg leading-relaxed">This is not simply architectural design. Behind ULB stands an engineering ecosystem:</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {['Protective Technology', 'Nuclear & Industrial Engineering', 'Heavy Manufacturing', 'International Project Experience'].map((c) => (
                <span key={c} className="px-4 py-2 text-xs sm:text-sm rounded-sm border border-zinc-800 text-zinc-200" style={{ backgroundColor: '#0c0e11' }}>{c}</span>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* NUCLEAR-GRADE → PRIVATE */}
      <Section className="relative py-24 sm:py-32 border-t border-zinc-900" style={{ backgroundImage: `linear-gradient(rgba(8,9,11,0.85), rgba(8,9,11,0.95)), url(${IMG.nuclear})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-5xl font-bold font-['Syne',sans-serif] leading-tight">NUCLEAR-GRADE ENGINEERING.<br /><span style={{ color: GOLD }}>PRIVATE-GRADE COMFORT.</span></h2>
          <div className="mt-12 flex flex-wrap gap-3 text-sm text-zinc-300">
            {['Massive protective structures', 'Underground engineering', 'Controlled environments', 'Redundant systems', 'Ventilation', 'Filtration', 'Independent energy', 'Water management', 'Physical security', 'Long-term reliability'].map((x, i) => (
              <span key={i} className="px-4 py-2 border border-zinc-700 rounded-sm bg-[#0a0b0d]/50">{x}</span>
            ))}
          </div>
          <p className="mt-10 text-zinc-400 max-w-2xl text-lg">The engineering remains extreme — the environment becomes personal.</p>
        </div>
      </Section>

      {/* PRIVATE SECURE ENVIRONMENT */}
      <Section className="py-20 sm:py-28 border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Kicker>Bespoke by design</Kicker>
          <h2 className="text-3xl sm:text-5xl font-bold font-['Syne',sans-serif] leading-tight">NOT A SHELTER.<br />A PRIVATE SECURE ENVIRONMENT.</h2>
          <div className="mt-12 flex flex-wrap gap-2.5">
            {spaces.map((s, i) => (
              <span key={i} className="px-4 py-2.5 text-sm text-zinc-300 border border-zinc-800 rounded-sm hover:border-zinc-500 hover:text-white transition-colors">{s}</span>
            ))}
          </div>
          <p className="mt-8 text-zinc-500">Configurations are completely bespoke. No fixed floor plans, no standard models.</p>
        </div>
      </Section>

      {/* BESPOKE SCALE */}
      <Section className="py-20 sm:py-28 bg-[#0c0e11] border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12">
          <div>
            <Kicker>Scale</Kicker>
            <h2 className="text-3xl sm:text-4xl font-bold font-['Syne',sans-serif] leading-tight">NO STANDARD SIZE.<br />NO STANDARD LIFE.</h2>
            <p className="mt-6 text-zinc-400 text-lg">Projects are designed around the client — not around a catalogue. From compact private protective environments to underground residences covering thousands of square metres.</p>
          </div>
          <div className="grid grid-cols-2 gap-px bg-zinc-900 border border-zinc-900 self-start">
            {['Residents', 'Personnel', 'Location', 'Geology', 'Security requirements', 'Autonomy period', 'Lifestyle', 'Special functions'].map((x, i) => (
              <div key={i} className="bg-[#0c0e11] p-5 text-sm text-zinc-300">{x}</div>
            ))}
          </div>
        </div>
      </Section>

      {/* AUTONOMY */}
      <Section className="py-20 sm:py-28 border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Kicker>Autonomy by design</Kicker>
          <h2 className="text-3xl sm:text-4xl font-bold font-['Syne',sans-serif]">Engineered independence</h2>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900">
            {autonomySys.map((s, i) => (
              <div key={i} className="bg-[#0a0b0d] p-6 flex items-center gap-3">
                <s.icon className="w-5 h-5" style={{ color: GOLD }} /><span className="text-sm text-zinc-300 uppercase tracking-wide">{s.t}</span>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            {['30 days', '6 months', '12 months', 'Custom'].map((x, i) => (
              <span key={i} className="px-5 py-2.5 text-sm border border-zinc-700 rounded-sm text-zinc-200">{x}</span>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-3 text-xs uppercase tracking-widest text-zinc-500">
            <span className="px-4 py-2 border rounded-sm" style={{ borderColor: GOLD, color: GOLD }}>Primary</span>
            <ChevronRight className="w-4 h-4" /><span className="px-4 py-2 border border-zinc-700 rounded-sm">Backup</span>
            <ChevronRight className="w-4 h-4" /><span className="px-4 py-2 border border-zinc-700 rounded-sm">Emergency</span>
          </div>
        </div>
      </Section>

      {/* ADVANCED ENERGY & RESOURCE AUTONOMY */}
      <Section id="resource-autonomy" className="relative py-20 sm:py-28 border-t border-zinc-900 bg-[#0c0e11] overflow-hidden">
        <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)', backgroundSize: '52px 52px' }} />
        <div aria-hidden className="pointer-events-none absolute -top-32 right-0 w-[520px] h-[520px] rounded-full opacity-[0.06] blur-3xl" style={{ background: `radial-gradient(circle, ${GOLD}, transparent 70%)` }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="resource-autonomy-section">
          <Kicker>Autonomous by engineering</Kicker>
          <h2 className="text-3xl sm:text-4xl font-bold font-['Syne',sans-serif] leading-tight max-w-4xl">ADVANCED ENERGY &amp; RESOURCE AUTONOMY</h2>
          <div className="mt-6 max-w-3xl space-y-4 text-zinc-400 text-lg leading-relaxed">
            <p>ULB is designed not simply as a protected underground structure, but as a complete autonomous engineering environment capable of supporting independent life and operation for extended periods.</p>
            <p>Through the wider engineering and technological ecosystem behind SYNERGOATOM SE and TRIUMPH Alliance — including experience and strategic activities in nuclear energy, critical infrastructure and advanced energy engineering — ULB projects can incorporate sophisticated solutions for long-term energy and resource independence.</p>
            <p>Each project is individually engineered according to its location, required autonomy, available infrastructure and client requirements.</p>
          </div>

          {/* ENERGY + WATER */}
          <div className="mt-16 grid lg:grid-cols-2 gap-6 lg:gap-8">
            {/* ENERGY */}
            <div className="border border-zinc-800 rounded-sm bg-[#0a0b0d] p-7 sm:p-9">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-sm border border-zinc-800" style={{ color: GOLD }}><Zap className="w-5 h-5" /></span>
                <h3 className="text-2xl font-bold font-['Syne',sans-serif] tracking-wide">ENERGY</h3>
              </div>
              <p className="mt-5 text-zinc-400 leading-relaxed">The energy architecture may integrate an appropriate combination of:</p>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900">
                {energyItems.map((s, i) => (
                  <div key={i} className="bg-[#0a0b0d] p-4 flex items-center gap-3" data-testid={`energy-item-${i}`}>
                    <s.icon className="w-4 h-4 shrink-0" style={{ color: GOLD }} />
                    <span className="text-sm text-zinc-300 leading-snug">{s.t}</span>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-xs text-zinc-500 leading-relaxed">For exceptional projects requiring substantially higher levels of long-term energy independence, the engineering concept may also take into consideration emerging and strategic technologies associated with small-scale nuclear energy and related advanced energy systems — subject to applicable regulation, licensing, technological availability, location and project feasibility.</p>
              <p className="mt-3 text-xs text-zinc-600 leading-relaxed">The technological environment behind ULB extends from conventional and renewable autonomous systems to some of the most advanced areas of modern energy engineering.</p>
            </div>

            {/* WATER */}
            <div className="border border-zinc-800 rounded-sm bg-[#0a0b0d] p-7 sm:p-9">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-sm border border-zinc-800" style={{ color: GOLD }}><Droplets className="w-5 h-5" /></span>
                <h3 className="text-2xl font-bold font-['Syne',sans-serif] tracking-wide">WATER</h3>
              </div>
              <p className="mt-5 text-zinc-400 leading-relaxed">Long-term autonomy also requires an independent and resilient water strategy. Depending on geography, local resources and project requirements, ULB engineering may consider:</p>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900">
                {waterItems.map((s, i) => (
                  <div key={i} className="bg-[#0a0b0d] p-4 flex items-center gap-3" data-testid={`water-item-${i}`}>
                    <s.icon className="w-4 h-4 shrink-0" style={{ color: GOLD }} />
                    <span className="text-sm text-zinc-300 leading-snug">{s.t}</span>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-xs text-zinc-500 leading-relaxed">The ability to evaluate advanced water-management and independent water-supply solutions is adapted to different environments — including coastal, island, arid, remote and infrastructure-independent locations.</p>
              <p className="mt-3 text-[11px] text-zinc-600 leading-relaxed">*Desalination and alternative supply technologies considered only where geographically and technically appropriate.</p>
            </div>
          </div>

          {/* INTEGRATED AUTONOMY */}
          <div className="mt-16 border border-zinc-800 rounded-sm bg-[#0a0b0d] p-8 sm:p-12" data-testid="integrated-autonomy">
            <Kicker>Integrated autonomy</Kicker>
            <p className="text-zinc-400 leading-relaxed max-w-3xl">ULB autonomy is presented as one interconnected engineering system — each element engineered to support the others.</p>
            <div className="mt-8 flex flex-wrap items-center gap-x-2 gap-y-3">
              {integratedSystems.map((s, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <Plus className="w-3.5 h-3.5 text-zinc-600 shrink-0" />}
                  <span className="inline-flex items-center gap-2 px-4 py-2.5 border border-zinc-800 rounded-sm bg-[#0c0e11]">
                    <s.icon className="w-4 h-4" style={{ color: GOLD }} />
                    <span className="text-xs sm:text-sm uppercase tracking-wide text-zinc-200">{s.t}</span>
                  </span>
                </React.Fragment>
              ))}
            </div>
            <div className="mt-12 border-t border-zinc-800 pt-10">
              <p className="text-2xl sm:text-3xl md:text-4xl font-bold font-['Syne',sans-serif] leading-tight">
                <span className="block text-white">Protection requires autonomy.</span>
                <span className="block" style={{ color: GOLD }}>Autonomy requires engineering.</span>
              </p>
              <div className="mt-8 max-w-3xl space-y-4 text-zinc-400 text-lg leading-relaxed">
                <p>A truly autonomous environment is not created by one generator, one water tank or one technology. It is engineered as an integrated, redundant and resilient infrastructure system adapted to the specific project.</p>
                <p>For exceptional projects, ULB is capable of evaluating both established and advanced strategic technologies across these interconnected systems — always subject to technical feasibility, site conditions, applicable regulation and individual project requirements.</p>
              </div>
            </div>
          </div>

          {/* UNDERGROUND AUTONOMOUS ENGINEERING SYSTEM */}
          <div className="mt-20 pt-14 border-t border-zinc-800" data-testid="underground-system-section">
          <Kicker>One integrated underground ecosystem</Kicker>
          <h2 className="text-3xl sm:text-4xl font-bold font-['Syne',sans-serif] leading-tight max-w-4xl">ULB — UNDERGROUND AUTONOMOUS ENGINEERING SYSTEM</h2>
          <p className="mt-6 max-w-3xl text-zinc-400 text-lg leading-relaxed">Not simply a reinforced underground structure — an independently engineered environment where protection, energy, water, air, waste recovery, communications, data and life support operate as interconnected layers of one autonomous system.</p>
          <UndergroundDiagram />
          <div className="mt-12 border-t border-zinc-800 pt-10">
            <p className="text-xl sm:text-2xl md:text-3xl font-bold font-['Syne',sans-serif] leading-tight">
              <span className="block text-white">Connected when possible.</span>
              <span className="block" style={{ color: GOLD }}>Independent when necessary.</span>
            </p>
            <p className="mt-6 text-zinc-400 text-lg leading-relaxed max-w-3xl">Autonomy is not only the preservation of life. It is also the preservation of knowledge — a curated, expandable offline environment that continues to serve residents, education and continuity throughout prolonged isolation.</p>
          </div>

          {/* DATA & KNOWLEDGE CORE detail */}
          <div className="mt-12 border border-zinc-800 rounded-sm bg-[#0a0b0d] p-8 sm:p-10" data-testid="knowledge-core-block">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-sm border border-zinc-800" style={{ color: DATACLR }}><Database className="w-5 h-5" /></span>
              <h3 className="text-2xl font-bold font-['Syne',sans-serif] tracking-wide">DATA &amp; KNOWLEDGE CORE</h3>
            </div>
            <p className="mt-5 max-w-3xl text-zinc-400 leading-relaxed">A protected on-site server and data infrastructure engineered to continue operating independently of external Internet connectivity — maintaining a comprehensive offline archive of human knowledge, education and cultural resources.</p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
              {[
                { icon: Server, t: 'Secure Local Servers' },
                { icon: Database, t: 'Redundant Data Storage' },
                { icon: BookOpen, t: 'Offline Digital Library' },
                { icon: Cpu, t: 'Local AI & Computing' },
                { icon: GraduationCap, t: 'Education & Learning Resources' },
                { icon: FileText, t: 'Scientific & Technical Databases' },
                { icon: Landmark, t: 'Cultural & Historical Archives' },
                { icon: Archive, t: 'Personal / Family Data Vault' },
                { icon: Radio, t: 'Local Network & Internal Comms' },
              ].map((s, i) => (
                <div key={i} className="bg-[#0a0b0d] p-4 flex items-center gap-3" data-testid={`knowledge-item-${i}`}>
                  <s.icon className="w-4 h-4 shrink-0" style={{ color: DATACLR }} />
                  <span className="text-sm text-zinc-300 leading-snug">{s.t}</span>
                </div>
              ))}
            </div>

            <p className="mt-8 text-xs uppercase tracking-wide text-zinc-500 mb-3">Offline collections may include</p>
            <div className="flex flex-wrap gap-2">
              {['Science', 'Medicine', 'Engineering', 'Agriculture', 'History', 'Literature', 'Culture', 'Languages', 'Education', 'Technical Manuals', 'Reference Materials'].map((c) => (
                <span key={c} className="px-3.5 py-1.5 text-xs rounded-sm border border-zinc-800 text-zinc-300" style={{ backgroundColor: '#0c0e11' }}>{c}</span>
              ))}
            </div>
            <p className="mt-6 text-xs text-zinc-600 leading-relaxed max-w-3xl">For family-oriented projects, a complete offline educational environment can be maintained for children and adults, independent of external networks. Scope and content are curated and expandable per project.</p>
          </div>
          </div>
        </div>
      </Section>

      {/* PROTECTION */}
      <Section className="py-20 sm:py-28 bg-[#0c0e11] border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Kicker>Protective engineering</Kicker>
          <h2 className="text-3xl sm:text-4xl font-bold font-['Syne',sans-serif]">Requirements, engineered</h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
            {protection.map((p, i) => (
              <div key={i} className="bg-[#0c0e11] p-6 flex items-center gap-3 text-zinc-300"><ShieldCheck className="w-5 h-5" style={{ color: GOLD }} />{p}</div>
            ))}
          </div>
        </div>
      </Section>

      {/* LUXURY INTERIORS */}
      <Section className="py-0 border-t border-zinc-900">
        <div className="grid lg:grid-cols-2">
          <div className="p-8 sm:p-16 flex flex-col justify-center bg-[#0a0b0d]">
            <Kicker>The environment</Kicker>
            <h2 className="text-3xl sm:text-5xl font-bold font-['Syne',sans-serif] leading-tight">SECURITY WITHOUT COMPROMISE.<br /><span style={{ color: GOLD }}>COMFORT WITHOUT EXPOSURE.</span></h2>
            <p className="mt-6 text-zinc-400 text-lg leading-relaxed">Contemporary European architecture. Natural stone, timber, architectural concrete, sophisticated indirect lighting and museum-level detailing.</p>
            <p className="mt-6 text-zinc-300 text-lg">Behind the protective shell is not a shelter. It is a home.</p>
          </div>
          <TechOverlayImage src={IMG.interior} variant="a" testid="luxury-interior-image" />
        </div>
        <div className="grid lg:grid-cols-2">
          <TechOverlayImage src={IMG.spa} variant="b" className="order-2 lg:order-1" testid="luxury-spa-image" />
          <div className="p-8 sm:p-16 flex flex-col justify-center bg-[#0c0e11] order-1 lg:order-2">
            <div className="flex flex-wrap gap-2.5">
              {['Private spa', 'Cinema', 'Library', 'Office', 'Bedroom suites', 'Acoustic comfort', 'Natural materials'].map((x, i) => (
                <span key={i} className="px-4 py-2 text-sm text-zinc-300 border border-zinc-800 rounded-sm">{x}</span>
              ))}
            </div>
            <p className="mt-8 text-zinc-500">The environment feels like a serious private residence — designed by a serious architect.</p>
          </div>
        </div>
      </Section>

      {/* GLOBAL DELIVERY */}
      <Section className="py-20 sm:py-28 border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-6">
            <Globe2 className="w-10 h-10 mt-1 flex-shrink-0" style={{ color: GOLD }} />
            <div>
              <Kicker>Global capability</Kicker>
              <h2 className="text-3xl sm:text-4xl font-bold font-['Syne',sans-serif]">ENGINEERED HERE. BUILT ANYWHERE.</h2>
              <p className="mt-6 max-w-2xl text-zinc-400 text-lg">Projects can be developed worldwide, subject to local regulatory and licensing requirements.</p>
            </div>
          </div>
          <div className="mt-12 flex flex-wrap gap-3 text-sm text-zinc-300">
            {['Geology', 'Climate', 'Seismic conditions', 'Local regulations', 'Security requirements', 'Available infrastructure', 'Client-specific risk profile'].map((x, i) => (
              <span key={i} className="px-4 py-2 border border-zinc-800 rounded-sm">{x}</span>
            ))}
          </div>
        </div>
      </Section>

      {/* TURNKEY */}
      <Section className="py-20 sm:py-28 bg-[#0c0e11] border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Kicker>Turnkey delivery</Kicker>
          <h2 className="text-3xl sm:text-4xl font-bold font-['Syne',sans-serif]">ONE PROJECT. ONE RESPONSIBILITY.</h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
            {turnkey.map((t, i) => (
              <div key={i} className="bg-[#0c0e11] p-6 flex items-baseline gap-4 hover:bg-[#12151a] transition-colors">
                <span className="text-xl font-mono" style={{ color: GOLD }}>{String(i + 1).padStart(2, '0')}</span>
                <span className="text-zinc-200">{t}</span>
              </div>
            ))}
          </div>
          <p className="mt-8 text-zinc-500 max-w-3xl">SYNERGOATOM coordinates the complete technological project structure, using appropriate TRIUMPH Alliance capabilities, specialized partners and locally licensed contractors where required.</p>
        </div>
      </Section>

      {/* CONFIDENTIALITY */}
      <Section className="py-24 sm:py-32 border-t border-zinc-900 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Lock className="w-8 h-8 mx-auto mb-6" style={{ color: GOLD }} />
          <h2 className="text-3xl sm:text-5xl font-bold font-['Syne',sans-serif]">CONFIDENTIAL BY DEFINITION.</h2>
          <p className="mt-6 text-zinc-400 text-lg">Private protective infrastructure requires a different approach to information. Project location, architecture, protective characteristics, engineering systems and client identity can be managed under controlled-access project structures.</p>
          <div className="mt-10 flex flex-wrap justify-center gap-3 text-xs uppercase tracking-widest text-zinc-500">
            {['Need-to-know access', 'Controlled documentation', 'Separated contractor information', 'Secure project communication', 'Client confidentiality'].map((x, i) => (
              <span key={i} className="px-4 py-2 border border-zinc-800 rounded-sm">{x}</span>
            ))}
          </div>
        </div>
      </Section>

      {/* QUESTIONNAIRE */}
      <Section id="brief" className="py-20 sm:py-28 border-t border-zinc-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Kicker>Private Project Brief</Kicker>
            <h2 className="text-3xl sm:text-4xl font-bold font-['Syne',sans-serif]">REQUEST A CONFIDENTIAL CONSULTATION</h2>
            <p className="mt-5 text-zinc-400">The first confidential step of a private architectural and engineering project. Answer only what you know — our engineers determine the technical solution afterwards.</p>
          </div>
          <ProjectBrief />
        </div>
      </Section>

      {/* FINAL BRAND MOMENT */}
      <section className="relative py-28 sm:py-40 border-t border-zinc-900 md:bg-fixed" style={{ backgroundImage: `linear-gradient(rgba(8,9,11,0.82), rgba(8,9,11,0.92)), url(${IMG.hero})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl sm:text-7xl font-bold font-['Syne',sans-serif] tracking-tight">ULB</h2>
          <p className="mt-3 text-2xl sm:text-3xl font-bold text-zinc-200 font-['Syne',sans-serif]">ULTIMATE LUXURY BUNKERS</p>
          <p className="mt-4 text-zinc-300 tracking-widest uppercase text-sm">Private Protective Infrastructure · Engineered by SYNERGOATOM SE</p>
          <p className="mt-8 text-zinc-400">Nuclear Engineering. Protective Infrastructure. Advanced Materials. Private Architecture.</p>
          <button onClick={() => scrollTo('brief')} className="mt-10 px-8 py-4 font-semibold rounded-sm text-[#0a0b0d] inline-flex items-center gap-2 hover:-translate-y-0.5 transition-transform" style={{ backgroundColor: GOLD }}>REQUEST A CONFIDENTIAL CONSULTATION<ArrowRight className="w-4 h-4" /></button>
        </div>
      </section>

      <SiteFooter lang={lang} />
    </div>
  );
}

/* ---------------- PRIVATE PROJECT BRIEF (multi-step) ---------------- */

const STEPS = [
  { key: 'purpose', title: 'Project Intent', q: 'What is the primary purpose of the project?', type: 'multi', help: 'Every secure environment begins with a different objective.', options: ['Private secure residence', 'Emergency shelter', 'Long-term autonomous residence', 'Secondary secure residence', 'Protected extension of an existing residence', 'Family protection', 'Business continuity / executive protection', 'Secure estate infrastructure', 'Protection of valuable assets', 'Other'] },
  { key: 'usage', title: 'Intended Use', q: 'How do you expect to use the facility?', type: 'single', options: ['Emergency use only', 'Occasional stays', 'Regular use', 'Seasonal residence', 'Permanent underground residence', 'Mixed above-ground / underground living', 'Primarily secure infrastructure with residential capability', 'Not yet decided'] },
  { key: 'risk', title: 'Private Risk Profile', q: 'What should the facility protect against?', type: 'multi', help: 'Select scenarios for our engineering team to consider. You do not need to determine technical protection levels.', options: ['Conventional armed conflict', 'Blast / explosion', 'Ballistic threats', 'Terrorist attack', 'Civil unrest', 'Forced intrusion', 'Nuclear incident', 'Nuclear conflict / fallout', 'Radiological contamination', 'Chemical contamination', 'Biological contamination', 'Extreme weather', 'Flooding', 'Earthquake', 'Long-term power-grid failure', 'Extended isolation from external infrastructure', 'Prefer to discuss privately'] },
  { key: 'risk_note', title: 'Private Risk Profile', q: 'Is there a specific scenario that concerns you most?', type: 'textarea', optional: true, placeholder: 'Describe any scenario, concern or requirement you would like our engineering team to understand.' },
  { key: 'cbrn_expectations', title: 'CBRN Considerations', q: 'For nuclear / CBRN scenarios, what should we assume?', type: 'multi', optional: true, showIf: (a) => (a.risk || []).some((x) => /nuclear|radiolog|chemical|biological/i.test(x)), options: ['Extended occupancy duration', 'High CBRN protection expectation', 'Decontamination area required', 'Complete independence from external infrastructure', 'Engineering recommendation required'] },
  { key: 'philosophy', title: 'Protection Priorities', q: 'Desired overall philosophy', type: 'single', options: ['Maximum achievable protection', 'High protection balanced with architectural comfort', 'Primarily luxury residence with enhanced protection', 'Primarily emergency infrastructure', 'Unsure — recommend an appropriate solution'] },
  { key: 'location', title: 'Location', q: 'Where is the project located?', type: 'fields', fields: [{ k: 'country', l: 'Country' }, { k: 'region', l: 'Region / State / Province' }, { k: 'city', l: 'Nearest city' }], optional: true, help: 'You may prefer not to disclose the exact location at this stage.' },
  { key: 'site_status', title: 'Location', q: 'Site status', type: 'single', options: ['Site already owned', 'Property under acquisition', 'Existing residence', 'New development', 'Site not yet selected', 'Multiple locations under consideration'] },
  { key: 'site_conditions', title: 'Site Conditions', q: 'What is currently known about the site?', type: 'multi', optional: true, help: 'Preliminary only. Actual geological, seismic, groundwater and flood conditions must be determined by professional site investigation.', options: ['Urban', 'Suburban', 'Rural', 'Remote', 'Mountain', 'Coastal', 'Island', 'Forest', 'Rock geology', 'Soil / clay', 'High groundwater', 'Flood exposure', 'Seismic activity', 'Unknown'] },
  { key: 'area', title: 'Project Type', q: 'Approximate desired underground area', type: 'single', options: ['Under 100 m²', '100–250 m²', '250–500 m²', '500–1,000 m²', '1,000–2,500 m²', '2,500–5,000 m²', 'More than 5,000 m²', 'Not decided'] },
  { key: 'occupancy', title: 'Occupancy', q: 'Who will the facility accommodate?', type: 'numbers', optional: true, fields: [{ k: 'adults', l: 'Adults' }, { k: 'children', l: 'Children' }, { k: 'elderly', l: 'Elderly' }, { k: 'staff', l: 'Domestic staff' }, { k: 'security', l: 'Security personnel' }, { k: 'max', l: 'Max emergency occupancy' }] },
  { key: 'autonomy', title: 'Autonomy', q: 'Desired autonomous operating period', type: 'single', options: ['72 hours', '7 days', '30 days', '90 days', '6 months', '12 months', 'More than 12 months', 'Maximum technically practical', 'Unsure'] },
  { key: 'program', title: 'Architectural Program', q: 'Which spaces should the project include?', type: 'multi', optional: true, options: ['Private suites', 'Guest suites', 'Family living', 'Kitchen(s)', 'Long-term food storage', 'Office / study', 'Meeting / boardroom', 'Secure communications', 'Server / IT room', 'Medical room', 'Gym', 'Spa / sauna', 'Cinema', 'Library', 'Wine cellar', 'Art / collection storage', 'Safe room / vault', 'Pet facilities', 'Staff accommodation'] },
  { key: 'systems', title: 'Technical Systems', q: 'Any preferences for autonomous systems?', type: 'multi', optional: true, help: 'You do not need to engineer the system — recommendations can be provided.', options: ['Maximum energy independence', 'Solar / renewable integration', 'Battery storage', 'Independent fuel storage', 'Private well / borehole', 'Water treatment & storage', 'CBRN filtration', 'Overpressure / airlocks', 'Decontamination area', 'Engineering recommendation required'] },
  { key: 'comfort', title: 'Long-Term Comfort', q: 'For permanent underground living, prioritise:', type: 'multi', optional: true, showIf: (a) => a.usage === 'Permanent underground residence', options: ['Daylight simulation / circadian lighting', 'Acoustic comfort', 'Indoor vegetation / hydroponics', 'Exercise & recreation', 'Workspace', 'Education / children', 'Long-term storage'] },
  { key: 'security', title: 'Security', q: 'Preliminary security requirements', type: 'multi', optional: true, help: 'Detailed security architecture is discussed privately in later phases.', options: ['Controlled access', 'Biometric access', 'CCTV', 'Intrusion detection', 'Security control room', 'Panic / safe room', 'Protected communications'] },
  { key: 'character', title: 'Architectural Character', q: 'Preferred atmosphere', type: 'multi', optional: true, options: ['Contemporary minimalism', 'Warm contemporary', 'Modern European', 'Industrial luxury', 'Natural materials', 'Architectural concrete', 'Hotel-level luxury', 'Residential / home-like', 'Highly technical'] },
  { key: 'logistics', title: 'Delivery & Logistics', q: 'Site accessibility & logistics (if known)', type: 'multi', optional: true, showIf: (a) => (a.site_conditions || []).some((x) => /remote|island|mountain/i.test(x)) || ['Remote', 'Multiple locations under consideration'].includes(a.site_status), options: ['Accessible by heavy trucks', 'Crane access', 'Container transport possible', 'Marine logistics required', 'Oversized transport restrictions', 'Prefer maximum local contractors', 'Combination of international & local teams', 'Recommendation required'] },
  { key: 'timing', title: 'Project Timing', q: 'Desired timing', type: 'single', optional: true, options: ['As soon as practically possible', 'Within 12 months', '1–2 years', 'Flexible', 'Not yet determined'] },
  { key: 'investment', title: 'Project Scale', q: 'Have you established an investment framework?', type: 'single', optional: true, help: 'This helps us determine the appropriate engineering and delivery approach. It does not constitute a quotation.', options: ['Not yet', 'Prefer to discuss privately', 'Yes — will discuss ranges privately'] },
  { key: 'priorities', title: 'What Matters Most', q: 'Your three most important priorities', type: 'multi', optional: true, max: 3, options: ['Maximum protection', 'Maximum autonomy', 'Privacy', 'Family safety', 'Architectural quality', 'Comfort', 'Fast delivery', 'Long service life', 'Low maintenance', 'Concealment', 'Technology', 'Sustainability'] },
  { key: 'open_brief', title: 'Open Client Brief', q: 'Tell us what we have not asked.', type: 'textarea', optional: true, placeholder: 'Anything unusual, personal, technical or operational that should influence the design. Unusual requirements are welcome.' },
  { key: 'documents', title: 'Supporting Documents', q: 'Upload any supporting documents', type: 'upload', optional: true, help: 'Optional and confidential. Site plans, geological or geotechnical surveys, architectural drawings, site photographs or reference imagery help our engineering team assess your project. Accepted: PDF, DOC, DOCX, JPG, PNG, WEBP, GIF · up to 25 MB each.' },
];

const CONTACT_FIELDS = [
  { k: 'role', l: 'You are', type: 'select', options: ['Principal / Owner', 'Family member', 'Family Office', 'Architect', 'Engineer', 'Security Adviser', 'Legal / Personal Representative', 'Developer', 'Other'] },
  { k: 'name', l: 'Name' }, { k: 'organization', l: 'Organization (optional)' }, { k: 'country', l: 'Country' },
  { k: 'email', l: 'Email', type: 'email' }, { k: 'phone', l: 'Telephone / secure contact' },
  { k: 'preferred', l: 'Preferred method & time for contact' },
];

const ProjectBrief = () => {
  const total = STEPS.length + 2; // +contact +review
  const [answers, setAnswers] = useState(() => { try { return JSON.parse(localStorage.getItem('lb_brief') || '{}'); } catch { return {}; } });
  const [contact, setContact] = useState(() => { try { return JSON.parse(localStorage.getItem('lb_contact') || '{}'); } catch { return {}; } });
  const [step, setStep] = useState(0);
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState('idle');
  const [files, setFiles] = useState(() => { try { return JSON.parse(localStorage.getItem('lb_files') || '[]'); } catch { return []; } });
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => { localStorage.setItem('lb_brief', JSON.stringify(answers)); }, [answers]);
  useEffect(() => { localStorage.setItem('lb_contact', JSON.stringify(contact)); }, [contact]);
  useEffect(() => { localStorage.setItem('lb_files', JSON.stringify(files)); }, [files]);

  const handleFiles = async (fileList) => {
    const arr = Array.from(fileList || []);
    if (!arr.length) return;
    setUploadError('');
    setUploading(true);
    for (const f of arr) {
      if (f.size > 25 * 1024 * 1024) { setUploadError(`${f.name} exceeds the 25 MB limit.`); continue; }
      try {
        const fd = new FormData();
        fd.append('file', f);
        const res = await fetch(`${API}/api/bunker-inquiries/upload`, { method: 'POST', body: fd });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          setUploadError(err.detail || `Could not upload ${f.name}.`);
          continue;
        }
        const data = await res.json();
        setFiles((prev) => [...prev, data]);
      } catch (e) { setUploadError(`Could not upload ${f.name}. Please try again.`); }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (id) => setFiles((prev) => prev.filter((x) => x.id !== id));
  const fmtSize = (b) => b >= 1024 * 1024 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;
  const isImage = (ct) => (ct || '').startsWith('image/');

  const visibleSteps = STEPS.filter((s) => !s.showIf || s.showIf(answers));
  const isContact = step === visibleSteps.length;
  const isReview = step === visibleSteps.length + 1;
  const pct = Math.round((step / (visibleSteps.length + 1)) * 100);

  const setVal = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));
  const toggleMulti = (k, v, max) => setAnswers((a) => {
    const cur = a[k] || []; let next;
    if (cur.includes(v)) next = cur.filter((x) => x !== v);
    else { if (max && cur.length >= max) return a; next = [...cur, v]; }
    return { ...a, [k]: next };
  });

  const next = () => setStep((s) => Math.min(s + 1, visibleSteps.length + 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setStatus('sending');
    try {
      const res = await fetch(`${API}/api/bunker-inquiries`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers, contact, files }),
      });
      if (!res.ok) throw new Error('bad');
      setStatus('done');
      localStorage.removeItem('lb_brief'); localStorage.removeItem('lb_contact'); localStorage.removeItem('lb_files');
    } catch (e) { setStatus('error'); }
  };

  if (status === 'done') {
    return (
      <div className="border border-zinc-800 rounded-sm p-10 text-center bg-[#0c0e11]" data-testid="brief-success">
        <Check className="w-10 h-10 mx-auto mb-4" style={{ color: GOLD }} />
        <h3 className="text-2xl font-bold font-['Syne',sans-serif]">Thank you.</h3>
        <p className="mt-3 text-zinc-400">Your project brief has been received. A SYNERGOATOM representative will review the information and contact you regarding the appropriate next step.</p>
      </div>
    );
  }

  const chip = (active) => `px-4 py-2.5 text-sm rounded-sm border transition-colors cursor-pointer ${active ? 'text-[#0a0b0d]' : 'text-zinc-300 border-zinc-700 hover:border-zinc-400'}`;

  return (
    <div className="border border-zinc-800 rounded-sm bg-[#0c0e11]" data-testid="project-brief">
      {/* progress */}
      <div className="px-6 pt-6">
        <div className="flex items-center justify-between text-xs text-zinc-500 mb-2">
          <span>{isReview ? 'Review' : isContact ? 'Contact & Representation' : `Step ${step + 1} · ${visibleSteps[step].title}`}</span>
          <span>{pct}%</span>
        </div>
        <div className="h-1 bg-zinc-800 rounded-full overflow-hidden"><div className="h-full transition-all duration-300" style={{ width: `${pct}%`, backgroundColor: GOLD }} /></div>
      </div>

      <div className="p-6 sm:p-8 min-h-[320px]">
        {!isContact && !isReview && (() => {
          const s = visibleSteps[step];
          return (
            <div>
              <h3 className="text-xl sm:text-2xl font-semibold font-['Syne',sans-serif]">{s.q}</h3>
              {s.help && <p className="mt-2 text-sm text-zinc-500">{s.help}</p>}
              {s.optional && <p className="mt-1 text-xs text-zinc-600">Optional · you may skip</p>}
              {s.max && <p className="mt-1 text-xs text-zinc-600">Select up to {s.max}</p>}

              {(s.type === 'multi' || s.type === 'single') && (
                <div className="mt-6 flex flex-wrap gap-2.5">
                  {s.options.map((o) => {
                    const active = s.type === 'multi' ? (answers[s.key] || []).includes(o) : answers[s.key] === o;
                    return <button key={o} data-testid={`opt-${s.key}`} onClick={() => s.type === 'multi' ? toggleMulti(s.key, o, s.max) : setVal(s.key, o)} className={chip(active)} style={active ? { backgroundColor: GOLD, borderColor: GOLD } : {}}>{o}</button>;
                  })}
                </div>
              )}
              {s.type === 'textarea' && (
                <textarea data-testid={`ta-${s.key}`} value={answers[s.key] || ''} onChange={(e) => setVal(s.key, e.target.value)} placeholder={s.placeholder} rows={5} className="mt-6 w-full bg-[#0a0b0d] border border-zinc-800 rounded-sm p-4 text-zinc-100 focus:border-zinc-500 outline-none" />
              )}
              {(s.type === 'fields' || s.type === 'numbers') && (
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {s.fields.map((f) => (
                    <div key={f.k}>
                      <label className="text-xs text-zinc-500 uppercase tracking-wide">{f.l}</label>
                      <input type={s.type === 'numbers' ? 'number' : 'text'} min="0" value={(answers[s.key] || {})[f.k] || ''} onChange={(e) => setVal(s.key, { ...(answers[s.key] || {}), [f.k]: e.target.value })} className="mt-1 w-full bg-[#0a0b0d] border border-zinc-800 rounded-sm p-3 text-zinc-100 focus:border-zinc-500 outline-none" />
                    </div>
                  ))}
                </div>
              )}
              {s.type === 'upload' && (
                <div className="mt-6">
                  <input
                    ref={fileInputRef}
                    data-testid="brief-file-input"
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.gif,image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    className="hidden"
                    onChange={(e) => handleFiles(e.target.files)}
                  />
                  <button
                    type="button"
                    data-testid="brief-upload-trigger"
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    disabled={uploading}
                    className="w-full border border-dashed border-zinc-700 hover:border-zinc-500 rounded-sm py-10 flex flex-col items-center justify-center text-zinc-400 hover:text-zinc-200 transition-colors disabled:opacity-50"
                  >
                    {uploading ? <Loader2 className="w-7 h-7 mb-3 animate-spin" /> : <UploadCloud className="w-7 h-7 mb-3" />}
                    <span className="text-sm">{uploading ? 'Uploading…' : 'Click to select files'}</span>
                    <span className="text-xs text-zinc-600 mt-1">PDF, DOC, DOCX, JPG, PNG, WEBP, GIF · up to 25 MB each</span>
                  </button>
                  {uploadError && <p className="mt-3 text-sm text-red-400" data-testid="brief-upload-error">{uploadError}</p>}
                  {files.length > 0 && (
                    <ul className="mt-5 space-y-2" data-testid="brief-file-list">
                      {files.map((f) => (
                        <li key={f.id} className="flex items-center justify-between gap-3 bg-[#0a0b0d] border border-zinc-800 rounded-sm px-4 py-3">
                          <span className="flex items-center gap-3 min-w-0">
                            {isImage(f.content_type) ? <ImageIcon className="w-4 h-4 shrink-0 text-zinc-400" /> : <FileText className="w-4 h-4 shrink-0 text-zinc-400" />}
                            <span className="truncate text-sm text-zinc-200">{f.original_filename}</span>
                            <span className="text-xs text-zinc-600 shrink-0">{fmtSize(f.size)}</span>
                          </span>
                          <button type="button" data-testid={`brief-file-remove-${f.id}`} onClick={() => removeFile(f.id)} aria-label={`Remove ${f.original_filename}`} className="text-zinc-500 hover:text-red-400 transition-colors shrink-0">
                            <X className="w-4 h-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {isContact && (
          <div>
            <h3 className="text-xl sm:text-2xl font-semibold font-['Syne',sans-serif]">Contact &amp; Representation</h3>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {CONTACT_FIELDS.map((f) => (
                <div key={f.k} className={f.k === 'preferred' ? 'sm:col-span-2' : ''}>
                  <label className="text-xs text-zinc-500 uppercase tracking-wide">{f.l}</label>
                  {f.type === 'select' ? (
                    <select value={contact[f.k] || ''} onChange={(e) => setContact({ ...contact, [f.k]: e.target.value })} className="mt-1 w-full bg-[#0a0b0d] border border-zinc-800 rounded-sm p-3 text-zinc-100 focus:border-zinc-500 outline-none">
                      <option value="">—</option>
                      {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input data-testid={`contact-${f.k}`} type={f.type || 'text'} value={contact[f.k] || ''} onChange={(e) => setContact({ ...contact, [f.k]: e.target.value })} className="mt-1 w-full bg-[#0a0b0d] border border-zinc-800 rounded-sm p-3 text-zinc-100 focus:border-zinc-500 outline-none" />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {isReview && (
          <div data-testid="brief-review">
            <h3 className="text-xl sm:text-2xl font-semibold font-['Syne',sans-serif]">Review &amp; submit</h3>
            <p className="mt-2 text-sm text-zinc-500">You can go back to edit any section.</p>
            <div className="mt-5 space-y-2 max-h-72 overflow-auto pr-2 text-sm">
              {visibleSteps.map((s) => {
                const v = answers[s.key];
                if (!v || (Array.isArray(v) && !v.length) || (typeof v === 'object' && !Array.isArray(v) && !Object.values(v).some(Boolean))) return null;
                const disp = Array.isArray(v) ? v.join(', ') : typeof v === 'object' ? Object.entries(v).filter(([, x]) => x).map(([k, x]) => `${k}: ${x}`).join(', ') : v;
                return <div key={s.key} className="border-b border-zinc-800 pb-2"><span className="text-zinc-500">{s.title}: </span><span className="text-zinc-200">{disp}</span></div>;
              })}
              {(contact.name || contact.email) && <div className="border-b border-zinc-800 pb-2"><span className="text-zinc-500">Contact: </span><span className="text-zinc-200">{contact.name} · {contact.email} · {contact.role}</span></div>}
              {files.length > 0 && <div className="border-b border-zinc-800 pb-2" data-testid="brief-review-files"><span className="text-zinc-500">Documents: </span><span className="text-zinc-200">{files.map((f) => f.original_filename).join(', ')}</span></div>}
            </div>
            <label className="mt-5 flex items-start gap-3 text-sm text-zinc-400 cursor-pointer">
              <input type="checkbox" data-testid="brief-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" />
              <span>I consent to SYNERGOATOM SE processing the information provided in this brief for the purpose of preparing a preliminary project assessment and arranging a private consultation.</span>
            </label>
            {status === 'error' && <p className="mt-3 text-sm text-red-400" data-testid="brief-error">Submission failed. Please try again.</p>}
          </div>
        )}
      </div>

      {/* nav */}
      <div className="flex items-center justify-between px-6 py-4 border-t border-zinc-800">
        <button onClick={back} disabled={step === 0} data-testid="brief-back" className="inline-flex items-center gap-1 text-sm text-zinc-400 disabled:opacity-30 hover:text-white"><ChevronLeft className="w-4 h-4" />Back</button>
        <span className="text-xs text-zinc-600">Saved automatically</span>
        {!isReview ? (
          <button onClick={next} data-testid="brief-next" className="inline-flex items-center gap-1 text-sm font-medium px-5 py-2 rounded-sm text-[#0a0b0d]" style={{ backgroundColor: GOLD }}>{isContact ? 'Review' : 'Next'}<ChevronRight className="w-4 h-4" /></button>
        ) : (
          <button onClick={submit} disabled={!consent || status === 'sending'} data-testid="brief-submit" className="inline-flex items-center gap-2 text-sm font-medium px-5 py-2 rounded-sm text-[#0a0b0d] disabled:opacity-40" style={{ backgroundColor: GOLD }}>{status === 'sending' ? 'Submitting…' : 'Submit Private Project Brief'}</button>
        )}
      </div>
    </div>
  );
};
